// src/pages/PathManagePatient/OpdIpdPatients.jsx
import React, { useEffect, useState, useRef } from "react";
import { Box, Button, Tabs, Tab, Typography, Paper, CircularProgress, IconButton, Alert, TableContainer, Table, TableHead, TableRow, TableBody, TableCell, TablePagination, styled, tableCellClasses, Tooltip, Modal, TextField, Stack, InputAdornment, MenuItem } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import PrintIcon from "@mui/icons-material/Print";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
  import SearchIcon from "@mui/icons-material/Search";
  import FilterAltIcon from "@mui/icons-material/FilterAlt";
  import ClearAllIcon from "@mui/icons-material/ClearAll";
  import DownloadIcon from "@mui/icons-material/Download";
  import dayjs from "dayjs";
import { toast } from "react-toastify";
import { STANDARD_MODAL_SURFACE_SX } from "../../utils/formModalStyles";
import { getDisplaySampleCollectedId } from "../../utils/sampleTransactionUtils";
import { getOpdLabTests, getPrescriptionsWithLabTest } from "../../services/prescriptionService";
import { getAllIPDLabTests, updateSampleCollection, updateRejectStatus } from "../../services/PatientlabTestService";
import { getDoctorById, getDoctorByUserId, getAllDoctors } from "../../services/doctorService";
import { getAllLabTests, getLabTestById } from "../../services/labtestService";
import InvoiceModal from "./InvoiceModal";
import CollectSampleModal from "./CollectSampleModal";
import OpdTestResultsModal from "./components/OpdTestResultsModal";
import IpdWorkflowCollectModal from "./components/IpdWorkflowCollectModal";
import IpdWorkflowResultsModal from "./components/IpdWorkflowResultsModal";
import { billOpdPrescription } from "./opdPrescriptionBilling";
import {
  aggregatePathLabReportsFromPrescriptions,
  applyPathReportToLabTest,
  buildIpdReportsByPatientFromRows,
  buildOpdTableRowsFromReports,
  enrichOpdPrescriptionWithReports,
  enrichOpdTableRowsDoctorNames,
  findExistingLabTestForReport,
  getLabTestCatalogMaps,
  getLabTestName,
  groupIpdTests,
  hasSubCategories,
  mergeCompletedDoctorNamesOntoRows,
} from "./labTestWorkflowUtils";
import {
  getResultDisplayRows,
  printPathologyLabReport,
} from "../path-lab/labReportDisplayUtils";
import { printPathologyLabInvoiceFromRecord } from "../../utils/invoiceReprintUtils";
import {
  getBilledTests,
  getCompletedTestDoctorNameMap,
  getOrgPathLabReportsFull,
  getPathPabReportByPatient,
  invalidateOrgPathLabReportsLiteCache,
  mergePathLabReportPreferFilled,
} from "../../services/pathLabTestReportService";
import {
  buildPrescriptionLabTestIndex,
  dedupeOpdPrescriptionsForDisplay,
  dedupePathLabReportRecords,
  dedupePrescriptionLabTests,
  getBoundPathLabReportId,
  getLabTestCatalogId,
  getPathLabReportId,
  getPathLabReportResultValue,
  isLabTestAlreadyOnPrescription,
  normalizePathLabReportFields,
  registerLabTestOnPrescriptionIndex,
} from "../../utils/labTestDedupeUtils";
import { useNavigate } from "react-router-dom";
import OpdReports from "../inPatientDetail/OpdReports";
import IpdReports from "../inPatientDetail/IpdReports";
import { useAuth } from "../../context/AuthContext";
import {
  filterByOrganization,
  getOrganizationId,
  getOrganizationName,
  fetchOrganizationProfile,
} from "../../utils/organizationUtils";
import { downloadStyledTableExcel } from "../../utils/patientTableExcelExport";
import { getAllPatients, getPatientById } from "../../services/patientService";
import { useSubscriptionCapabilities } from "../../hooks/useSubscriptionCapabilities";
import {
  invalidatePatientLabBookingCache,
  listLabBookingsForPatients,
} from "../../services/patientLabBookingService";
import { createPatientBookedTestMatcher } from "../../utils/patientBookedLabTest";
import BookedByPatientMark from "./components/BookedByPatientMark";
import {
  PATH_LAB_DATA_UPDATED_EVENT,
  notifyPathLabDataUpdated,
} from "../../utils/opdPatientsRefresh";
import {
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
  PAGE_TABS_BAR_SX,
  PAGE_TAB_LIST_PROPS,
} from "../../utils/pageLayoutStyles";

const hasPrintableLabResult = (reportOrRow) => {
  if (!reportOrRow) return false;
  const result =
    reportOrRow.result ??
    reportOrRow.testResult ??
    getPathLabReportResultValue(reportOrRow);
  if (result == null) return false;
  if (typeof result === "string") return result.trim().length > 0;
  if (typeof result === "object") return Object.keys(result).length > 0;
  return true;
};

const OpdIpdPatients = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // Get the logged-in user
  const { hasCapability, hasPathPackageAccess, loading: capabilitiesLoading } = useSubscriptionCapabilities();
  
  // Check capabilities for pathdesk access — Basic package never includes Path
  const hasPathOpdCapability = hasPathPackageAccess && hasCapability("Path OPD");
  const hasPathIpdCapability =
    hasPathPackageAccess && (hasCapability("Path IPD") || hasCapability("IPD"));
  
  const [tab, setTab] = useState("opd");
  const [fetching, setFetching] = useState({ opd: true, ipd: false });
  const [opdReports, setOpdReports] = useState([]);
  const [opdTableRows, setOpdTableRows] = useState([]);
  const [isTestBookedByPatient, setIsTestBookedByPatient] = useState(() => () => false);
  const [ipdLabTests, setIpdLabTests] = useState([]);
  const [pageOpd, setPageOpd] = useState(0);
  const [rowsPerPageOpd, setRowsPerPageOpd] = useState(25);
  const [pageIpd, setPageIpd] = useState(0);
  const [rowsPerPageIpd, setRowsPerPageIpd] = useState(25);
  const [ipdReportsByPatient, setIpdReportsByPatient] = useState({});
  const [selectedRow, setSelectedRow] = useState(null);
  const [openTimeline, setOpenTimeline] = useState(false);
  const [timelinePatientId, setTimelinePatientId] = useState(null);
  const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [openCollectSampleModal, setOpenCollectSampleModal] = useState(false);
  const [openOpdResultsModal, setOpenOpdResultsModal] = useState(false);
  const [openIpdCollectSampleModal, setOpenIpdCollectSampleModal] = useState(false);
  const [openIpdResultsModal, setOpenIpdResultsModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedIpdGroup, setSelectedIpdGroup] = useState(null);
  const [billProcessingKey, setBillProcessingKey] = useState(null);
  const [printingInvoiceKey, setPrintingInvoiceKey] = useState(null);
  const [printingReportKey, setPrintingReportKey] = useState(null);
  const [doctorNames, setDoctorNames] = useState({});
  // Survives staged OPD refreshes so freshly saved results are not overwritten by stale org embeds.
  const recentlySavedOpdResultsRef = useRef(new Map());
  const fetchOpdInFlightRef = useRef(false);
  const fetchOpdGenerationRef = useRef(0);
  const lastOpdFetchAtRef = useRef(0);
  const lastForceOpdFetchAtRef = useRef(0);
  const opdTableRowCountRef = useRef(0);
  const pathLabRefreshTimerRef = useRef(null);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPatientName, setFilterPatientName] = useState("");
  const [filterTestName, setFilterTestName] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      fontWeight: 600,
      fontSize: "0.7rem",
      lineHeight: 1.2,
      padding: "6px 8px",
      whiteSpace: "nowrap",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: "0.75rem",
      padding: "4px 8px",
      verticalAlign: "middle",
    },
  }));

  const pathTableSx = { tableLayout: "auto", width: "100%" };
  const tightCol = {
    width: "1%",
    whiteSpace: "nowrap",
    px: "4px",
  };
  const colW = {
    report: { ...tightCol },
    patientId: { ...tightCol },
    patientName: {
      ...tightCol,
      maxWidth: 108,
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    doctor: {
      ...tightCol,
      maxWidth: 88,
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    testName: { width: 108, maxWidth: 108 },
    result: { width: 240, minWidth: 200, pr: "16px" },
    remarks: {
      width: 140,
      minWidth: 100,
      maxWidth: 160,
      whiteSpace: "normal",
      wordBreak: "break-word",
      pl: "8px",
      pr: "4px",
    },
    date: { ...tightCol, pl: "4px", pr: "8px" },
    payment: { ...tightCol, pl: "8px" },
    sampleStatus: { ...tightCol },
    sampleId: { ...tightCol },
    action: { width: "1%", whiteSpace: "nowrap", px: "2px" },
  };
  const actionIconBtnSx = { ...TABLE_ICON_BUTTON_SX, p: 0.25 };
  const ellipsisCellSx = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const fetchOpd = async ({ force = false, fromEvent = false } = {}) => {
    // Avoid stacked event/visibility fetches; never block mount/manual/forced loads.
    if (!force && fetchOpdInFlightRef.current) {
      return;
    }
    // Soft event refresh shortly after a force refresh only races and shrinks the table.
    if (
      fromEvent &&
      !force &&
      Date.now() - lastForceOpdFetchAtRef.current < 10000
    ) {
      return;
    }
    if (
      fromEvent &&
      !force &&
      Date.now() - lastOpdFetchAtRef.current < 2500
    ) {
      return;
    }

    const generation = ++fetchOpdGenerationRef.current;
    fetchOpdInFlightRef.current = true;
    // Keep existing rows visible on soft refresh; spinner only when empty or forced.
    const showSpinner = force || opdTableRowCountRef.current <= 0;
    if (showSpinner) {
      setFetching((p) => ({ ...p, opd: true }));
    }
    try {
      if (force) {
        invalidatePatientLabBookingCache();
        invalidateOrgPathLabReportsLiteCache();
        lastForceOpdFetchAtRef.current = Date.now();
      }
      // Ensure organizationId is available before fetching
      const storedUser = localStorage.getItem("user");
      let organizationId = null;
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          organizationId = parsedUser?.organizationId || localStorage.getItem("organizationId");
          if (organizationId && typeof organizationId === 'string') {
            organizationId = parseInt(organizationId, 10);
          }
        } catch (e) {
          console.error("[OpdIpdPatients] Error parsing stored user:", e);
        }
      }
      
      if (!organizationId || organizationId <= 0) {
        console.warn("[OpdIpdPatients] No organizationId available, skipping OPD fetch");
        setOpdReports([]);
        setOpdTableRows([]);
        setFetching((p) => ({ ...p, opd: false }));
        fetchOpdInFlightRef.current = false;
        opdTableRowCountRef.current = 0;
        // Do not stamp lastOpdFetchAt — a follow-up fetch once orgId exists must proceed.
        return;
      }

      lastOpdFetchAtRef.current = Date.now();
      
      console.log(`[OpdIpdPatients] Fetching OPD data for organizationId: ${organizationId}`);

      const { catalogById, catalogByName } = await getLabTestCatalogMaps();

      const completedDoctorNamePromise = getCompletedTestDoctorNameMap().catch(() => new Map());
      let completedDoctorByReportId = null;
      completedDoctorNamePromise.then((doctorByReportId) => {
        if (!(doctorByReportId instanceof Map) || doctorByReportId.size === 0) return;
        completedDoctorByReportId = doctorByReportId;
        setOpdTableRows((prev) => mergeCompletedDoctorNamesOntoRows(prev, doctorByReportId));
      });

      const [allDoctors, orgPatients, prescriptionsRaw] = await Promise.all([
        getAllDoctors({ forceRefresh: false }).catch(() => []),
        getAllPatients().catch(() => []),
        getPrescriptionsWithLabTest()
          .then((data) =>
            Array.isArray(data) && data.length > 0 ? data : getOpdLabTests()
          )
          .catch(() => getOpdLabTests().catch(() => [])),
      ]);

      const doctorIdNameMap = new Map();
      (Array.isArray(allDoctors) ? allDoctors : []).forEach((doctor) => {
        const id = Number(doctor?.id ?? doctor?.doctorId);
        const userId = Number(doctor?.userId ?? doctor?.user_id);
        const rawName =
          (doctor?.name && String(doctor.name).trim()) ||
          `${doctor?.firstName || ""} ${doctor?.lastName || ""}`.trim();
        if (!rawName) return;
        const formattedName = rawName.toLowerCase().startsWith("dr")
          ? rawName
          : `Dr. ${rawName}`;
        if (!Number.isNaN(id) && id > 0) {
          doctorIdNameMap.set(id, formattedName);
        }
        if (!Number.isNaN(userId) && userId > 0) {
          doctorIdNameMap.set(userId, formattedName);
        }
      });

      let prescriptions = Array.isArray(prescriptionsRaw) ? prescriptionsRaw : [];
      console.log(`[OpdIpdPatients] Received ${prescriptions.length} prescriptions`);
      
      // Log sample prescription to see its structure
      if (Array.isArray(prescriptions) && prescriptions.length > 0) {
        console.log(`[OpdIpdPatients] Sample prescription:`, prescriptions[0]);
        console.log(`[OpdIpdPatients] Sample prescription keys:`, Object.keys(prescriptions[0]));
        console.log(`[OpdIpdPatients] Sample prescription organizationId:`, prescriptions[0]?.organizationId);
        console.log(`[OpdIpdPatients] Sample prescription patientId:`, prescriptions[0]?.patientId);
      }
      
      // Build patient name/id maps from parallel patient fetch (non-blocking for prescriptions).
      let orgPatientIds = new Set();
      const patientNameById = new Map();
      if (organizationId) {
        try {
          const orgPatientsList = Array.isArray(orgPatients) ? orgPatients : [];
          console.log(`[OpdIpdPatients] getAllPatients returned ${orgPatientsList.length} patients`);

          const filteredPatients = filterByOrganization(orgPatientsList, user);
          console.log(`[OpdIpdPatients] After filterByOrganization: ${filteredPatients.length} patients`);

          filteredPatients.forEach((patient) => {
            const patientId = patient?.id || patient?.patientId || patient?.patient_id;
            if (patientId) {
              const numericId = Number(patientId);
              orgPatientIds.add(numericId);
              const displayName =
                patient?.name ||
                [patient?.firstName, patient?.lastName].filter(Boolean).join(" ").trim() ||
                patient?.patientName ||
                null;
              if (displayName) patientNameById.set(numericId, displayName);
            }
          });

          console.log(`[OpdIpdPatients] Found ${orgPatientIds.size} patients in organization ${organizationId}`);
          if (orgPatientIds.size > 0) {
            console.log(`[OpdIpdPatients] Sample patient IDs:`, Array.from(orgPatientIds).slice(0, 5));
          } else if (orgPatientsList.length === 0) {
            console.warn(`[OpdIpdPatients] Patient list empty — skipping patient-id prescription filter (trust org-scoped APIs).`);
          } else {
            console.warn(`[OpdIpdPatients] No patients matched organization ${organizationId} after filter — skipping patient-id prescription filter.`);
          }
        } catch (patientError) {
          console.error(`[OpdIpdPatients] Error processing organization patients:`, patientError);
        }
      }
      
      // Apply organization filtering to prescriptions
      // Since backend doesn't include organizationId field and might not filter correctly,
      // we need to verify prescriptions belong to organization by checking patientIds
      if (organizationId && Array.isArray(prescriptions) && prescriptions.length > 0) {
        const hasOrgIdField = prescriptions.some(p => p?.organizationId != null || p?.organization_id != null);
        
        if (!hasOrgIdField) {
          // No prescriptions have organizationId field - verify by checking patientIds
          console.warn(`[OpdIpdPatients] ⚠️ Received ${prescriptions.length} prescriptions without organizationId field.`);
          console.log(`[OpdIpdPatients] Verifying prescriptions belong to organization ${organizationId} by checking patientIds...`);
          
          if (orgPatientIds.size === 0) {
            // Patient master may be slow/incomplete — do not wipe org-scoped prescription API data.
            console.warn(
              `[OpdIpdPatients] Skipping patient-id prescription filter (org patient list unavailable).`
            );
          } else {
            // Strict filtering: only keep prescriptions whose patientId exists in organization's patients
            // This ensures we only show prescriptions for patients that belong to this organization
            const originalCount = prescriptions.length;
            // Try multiple possible field names for patient ID in prescriptions
            const patientIdsInPrescriptions = new Set();
            prescriptions.forEach(p => {
              const pid = p?.patientId || p?.patient_id;
              if (pid) {
                patientIdsInPrescriptions.add(Number(pid));
              }
            });
            
            console.log(`[OpdIpdPatients] Unique patientIds in prescriptions: ${patientIdsInPrescriptions.size}`);
            if (patientIdsInPrescriptions.size > 0) {
              console.log(`[OpdIpdPatients] Sample patientIds from prescriptions:`, Array.from(patientIdsInPrescriptions).slice(0, 5));
            }
            
            // Check overlap
            const matchingPatientIds = Array.from(patientIdsInPrescriptions).filter(id => orgPatientIds.has(id));
            console.log(`[OpdIpdPatients] Matching patientIds: ${matchingPatientIds.length}`);
            
            prescriptions = prescriptions.filter(presc => {
              const patientId = presc?.patientId || presc?.patient_id;
              if (!patientId) {
                return false;
              }
              const belongsToOrg = orgPatientIds.has(Number(patientId));
              if (!belongsToOrg) {
                console.log(`[OpdIpdPatients] Excluding prescription ${presc?.id} - patientId ${patientId} not in organization ${organizationId}`);
              }
              return belongsToOrg;
            });
            
            if (matchingPatientIds.length === 0 && patientIdsInPrescriptions.size > 0) {
              // Log warning about backend data inconsistency
              console.warn(`[OpdIpdPatients] ⚠️ WARNING: No matching patient IDs found between prescriptions and organization patients!`);
              console.warn(`[OpdIpdPatients] Organization patient IDs:`, Array.from(orgPatientIds).slice(0, 10));
              console.warn(`[OpdIpdPatients] Prescription patient IDs:`, Array.from(patientIdsInPrescriptions).slice(0, 10));
              console.warn(`[OpdIpdPatients] Backend returned ${originalCount} prescriptions, but none belong to organization ${organizationId} patients.`);
              console.warn(`[OpdIpdPatients] This indicates backend is returning prescriptions from base table or different organization.`);
            }
            
            console.log(`[OpdIpdPatients] Filtered ${originalCount} prescriptions to ${prescriptions.length} belonging to organization ${organizationId}`);
          }
        } else {
          // Some prescriptions have organizationId - use filterByOrganization to filter properly
          prescriptions = filterByOrganization(prescriptions, user);
          console.log(`[OpdIpdPatients] After filterByOrganization: ${Array.isArray(prescriptions) ? prescriptions.length : 0} prescriptions`);
        }
      } else {
        // Use filterByOrganization utility for standard filtering
        prescriptions = filterByOrganization(prescriptions, user);
        console.log(`[OpdIpdPatients] After filterByOrganization: ${Array.isArray(prescriptions) ? prescriptions.length : 0} prescriptions`);
      }
      
      prescriptions = (Array.isArray(prescriptions) ? prescriptions : []).map((prescription) =>
        enrichOpdPrescriptionWithReports(prescription, null, catalogByName, catalogById)
      );
      const basePrescriptions = JSON.parse(JSON.stringify(prescriptions));

      // Fallback patient names from prescriptions when patient master is slow/incomplete.
      basePrescriptions.forEach((prescription) => {
        const pid = Number(prescription?.patientId ?? prescription?.patient_id);
        const pname = prescription?.patientName;
        if (!Number.isNaN(pid) && pid > 0 && pname && !patientNameById.has(pid)) {
          patientNameById.set(pid, pname);
        }
      });

      const patientDoctorMap = new Map();
      basePrescriptions.forEach((prescription) => {
        const patientId = Number(prescription?.patientId ?? prescription?.patient_id);
        const doctorName = prescription?.doctorName;
        if (
          !Number.isNaN(patientId) &&
          patientId > 0 &&
          doctorName &&
          String(doctorName).trim() &&
          String(doctorName).trim() !== "—" &&
          !patientDoctorMap.has(patientId)
        ) {
          patientDoctorMap.set(patientId, String(doctorName).trim());
        }
      });

      const applyDoctorNamesToRows = (rows, prescriptions) =>
        enrichOpdTableRowsDoctorNames(rows, {
          prescriptions,
          doctorByPatientId: patientDoctorMap,
          doctorIdNameMap,
        });

      const buildOpdPrescriptionRows = (prescList, supplementalReports = []) => {
        // Merge supplemental by reportId preferring filled doctor/result fields.
        const supplementalById = new Map();
        (Array.isArray(supplementalReports) ? supplementalReports : []).forEach((report) => {
          const id = report?.reportId ?? report?.id;
          if (id == null) return;
          const existing = supplementalById.get(id);
          supplementalById.set(
            id,
            existing ? mergePathLabReportPreferFilled(existing, report) : report
          );
        });

        let allReports = [
          ...aggregatePathLabReportsFromPrescriptions(prescList),
          ...Array.from(supplementalById.values()),
        ];
        try {
          allReports = Array.isArray(allReports) ? allReports : [];
          // Org-scoped path-lab APIs are authoritative — do not run filterByOrganization
          // here; mixed rows (some with organizationId, some without) would drop today's
          // billed-tests rows and hide patients from the queue.

          allReports = dedupePathLabReportRecords(
            allReports.map(normalizePathLabReportFields)
          );
        } catch (reportError) {
          console.error("Error processing path lab reports:", reportError);
          allReports = [];
        }

        const prescMap = new Map();
        const registerPrescription = (presc) => {
          if (!presc?.id) return;
          prescMap.set(presc.id, presc);
          const numericId = Number(presc.id);
          if (!Number.isNaN(numericId)) {
            prescMap.set(numericId, presc);
            prescMap.set(String(numericId), presc);
          }
        };
        (Array.isArray(prescList) ? prescList : []).forEach(registerPrescription);

        const resolvePrescription = (prescId) => {
          if (prescId == null || prescId === "") return null;
          return (
            prescMap.get(prescId) ||
            prescMap.get(Number(prescId)) ||
            prescMap.get(String(prescId)) ||
            null
          );
        };

        const prescTestMap = new Map();
        prescMap.forEach((presc, prescId) => {
          presc.patientLabTests = dedupePrescriptionLabTests(presc.patientLabTests);
          prescTestMap.set(prescId, buildPrescriptionLabTestIndex(presc.patientLabTests));
        });

        const mergeReportIntoPrescription = (presc, report, labTestIndex) => {
          const normalizedReport = normalizePathLabReportFields(report);
          const existingTest = findExistingLabTestForReport(presc.patientLabTests, normalizedReport);

          if (existingTest) {
            const existingCatalogId = getLabTestCatalogId(existingTest);
            const reportCatalogId = getLabTestCatalogId(normalizedReport);
            if (
              existingCatalogId != null &&
              reportCatalogId != null &&
              existingCatalogId !== reportCatalogId
            ) {
              return;
            }
            Object.assign(
              existingTest,
              applyPathReportToLabTest(existingTest, normalizedReport, catalogById, catalogByName)
            );
            return;
          }

          if (isLabTestAlreadyOnPrescription(labTestIndex, normalizedReport)) {
            const reportId = getPathLabReportId(normalizedReport);
            const reportCatalogId = getLabTestCatalogId(normalizedReport);
            const reportName = String(
              normalizedReport?.testName ||
                normalizedReport?.test_name ||
                normalizedReport?.name ||
                normalizedReport?.labTestName ||
                ""
            )
              .trim()
              .toLowerCase();
            const testToUpdate =
              (reportId != null
                ? presc.patientLabTests?.find(
                    (test) => getBoundPathLabReportId(test) === reportId
                  )
                : null) ||
              (reportCatalogId != null
                ? presc.patientLabTests?.find((test) => {
                    if (getLabTestCatalogId(test) !== reportCatalogId) return false;
                    const boundId = getBoundPathLabReportId(test);
                    // Never overwrite a lab test already bound to another report.
                    if (boundId != null && reportId != null && boundId !== reportId) {
                      return false;
                    }
                    const testName = String(
                      test?.testName || test?.test_name || test?.name || test?.labTestName || ""
                    )
                      .trim()
                      .toLowerCase();
                    if (reportName && testName && reportName !== testName) return false;
                    return true;
                  })
                : null);

            if (testToUpdate) {
              Object.assign(
                testToUpdate,
                applyPathReportToLabTest(testToUpdate, normalizedReport, catalogById, catalogByName)
              );
            }
            return;
          }

          if (!presc.patientLabTests) presc.patientLabTests = [];

          const normalizedStatus =
            normalizedReport.resultStatus ??
            normalizedReport.result_status ??
            normalizedReport.status ??
            "PENDING";

          const labTestEntry = applyPathReportToLabTest(
            {
              testName: "",
              resultStatus: normalizedStatus,
            },
            normalizedReport,
            catalogById,
            catalogByName
          );

          presc.patientLabTests.push(labTestEntry);
          registerLabTestOnPrescriptionIndex(labTestIndex, labTestEntry);
        };

        const reportsByPrescId = new Map();
        const reportsWithoutPresc = [];
        for (const report of allReports) {
          const reportPrescId = report.prescriptionId;
          const reportStatus = String(
            report.resultStatus ?? report.result_status ?? report.status ?? ""
          ).toUpperCase();

          if (!["PENDING", "BILLED", "COLLECTED", "COMPLETE", "COMPLETED", ""].includes(reportStatus)) {
            continue;
          }

          if (reportPrescId) {
            if (!reportsByPrescId.has(reportPrescId)) {
              reportsByPrescId.set(reportPrescId, []);
            }
            reportsByPrescId.get(reportPrescId).push(report);
          } else {
            reportsWithoutPresc.push(report);
          }
        }

        for (const [reportPrescId, reports] of reportsByPrescId.entries()) {
          const presc = resolvePrescription(reportPrescId);
          if (!presc) {
            reportsWithoutPresc.push(...reports);
            continue;
          }

          const prescKey = presc.id;
          const labTestIndex =
            prescTestMap.get(prescKey) || buildPrescriptionLabTestIndex();

          for (const report of reports) {
            mergeReportIntoPrescription(presc, report, labTestIndex);
          }

          prescTestMap.set(prescKey, labTestIndex);
        }

        const reportsByPatientId = new Map();
        for (const report of reportsWithoutPresc) {
          const patientId = report?.patientId || report?.patient_id;
          if (!patientId) continue;
          if (!reportsByPatientId.has(patientId)) {
            reportsByPatientId.set(patientId, []);
          }
          reportsByPatientId.get(patientId).push(report);
        }

        const isVirtualPrescription = (presc) =>
          String(presc?.id || "").startsWith("virtual_");

        const findExistingPrescriptionForPatient = (patientId, reportDate = null) => {
          const candidates = Array.from(prescMap.values()).filter(
            (presc) =>
              !isVirtualPrescription(presc) &&
              Number(presc.patientId) === Number(patientId)
          );
          if (!candidates.length) return null;

          const reportDay = reportDate ? dayjs(reportDate) : null;
          if (reportDay?.isValid()) {
            const sameDay = candidates.filter(
              (presc) =>
                presc.createdAt && dayjs(presc.createdAt).isSame(reportDay, "day")
            );
            if (sameDay.length) {
              return sameDay.sort((a, b) => Number(b.id) - Number(a.id))[0];
            }
          }

          return candidates.sort((a, b) => Number(b.id) - Number(a.id))[0];
        };

        const mergeOrphanReportsIntoPrescription = (presc, reports) => {
          const prescKey = presc.id;
          const labTestIndex =
            prescTestMap.get(prescKey) || buildPrescriptionLabTestIndex(presc.patientLabTests);

          for (const report of reports) {
            mergeReportIntoPrescription(presc, report, labTestIndex);
          }

          prescTestMap.set(prescKey, labTestIndex);
        };

        for (const [patientId, reports] of reportsByPatientId.entries()) {
          const reportDate =
            reports[0]?.reportDate || reports[0]?.createdAt || reports[0]?.date || null;
          const existingPresc = findExistingPrescriptionForPatient(patientId, reportDate);

          if (existingPresc) {
            mergeOrphanReportsIntoPrescription(existingPresc, reports);
            continue;
          }

          const firstReport = reports[0] || {};
          const doctorFromReports =
            reports.find(
              (r) => r.doctorName && String(r.doctorName).trim() && String(r.doctorName).trim() !== "—"
            )?.doctorName || "";
          const existingForDoctor = Array.from(prescMap.values()).find(
            (presc) =>
              !String(presc?.id || "").startsWith("virtual_") &&
              Number(presc.patientId) === Number(patientId) &&
              presc.doctorName &&
              String(presc.doctorName).trim() &&
              String(presc.doctorName).trim() !== "—"
          );
          const resolvedDoctorName =
            doctorFromReports ||
            firstReport.doctorName ||
            existingForDoctor?.doctorName ||
            patientDoctorMap.get(Number(patientId)) ||
            "—";
          const virtualPresc = {
            id: `virtual_${patientId}_${Date.now()}`,
            patientId: Number(patientId),
            patientName: firstReport.patientName || "Unknown Patient",
            doctorId: firstReport.doctorId || existingForDoctor?.doctorId || null,
            doctorName: resolvedDoctorName,
            createdAt: firstReport.createdAt || firstReport.reportDate || new Date().toISOString(),
            patientLabTests: reports.map((report) => ({
              testName: getLabTestName(report) || report.testName || "",
              labTestId: getLabTestCatalogId(report) ?? report.labTestId ?? report.testId,
              testId: getLabTestCatalogId(report) ?? report.testId ?? report.labTestId,
              resultStatus: report.resultStatus ?? report.result_status ?? report.status ?? "PENDING",
              result: getPathLabReportResultValue(report) || null,
              normalRange: report.normalRange || null,
              reportId: getPathLabReportId(report),
              reportDate: report.reportDate || firstReport.reportDate || null,
              doctorName:
                (report.doctorName && String(report.doctorName).trim()) ||
                resolvedDoctorName,
              doctorId: report.doctorId || existingForDoctor?.doctorId || null,
            })),
          };
          registerPrescription(virtualPresc);
        }

        const processedPrescriptions = Array.from(prescMap.values())
          .filter((prescription) => (prescription.patientLabTests || []).length > 0)
          .map((prescription) => {
            const enriched = enrichOpdPrescriptionWithReports(
              prescription,
              allReports,
              catalogByName,
              catalogById
            );
            const doctorName =
              enriched.doctorName && String(enriched.doctorName).trim() !== "—"
                ? enriched.doctorName
                : (enriched.patientLabTests || []).find(
                    (test) => test.doctorName && String(test.doctorName).trim() !== "—"
                  )?.doctorName || enriched.doctorName;
            return {
              ...enriched,
              doctorName: doctorName || enriched.doctorName,
              patientLabTests: dedupePrescriptionLabTests(
                (enriched.patientLabTests || []).map((test) => ({
                  ...test,
                  doctorName: test.doctorName || doctorName || enriched.doctorName || null,
                }))
              ),
            };
          });

        return dedupeOpdPrescriptionsForDisplay(processedPrescriptions);
      };

      const applyOpdData = (supplementalReports = []) => {
        if (generation !== fetchOpdGenerationRef.current) return;
        const prescriptions = buildOpdPrescriptionRows(basePrescriptions, supplementalReports);
        setOpdReports(prescriptions);

        // Merge by reportId with supplemental/patient API last so freshly saved results
        // win over stale prescription embeds and org list lag (edit modal already uses patient API).
        const byReportId = new Map();
        const upsertReport = (row) => {
          const normalized = normalizePathLabReportFields(row);
          const reportId = getPathLabReportId(normalized);
          if (reportId == null) return;
          const existing = byReportId.get(reportId);
          byReportId.set(
            reportId,
            existing ? mergePathLabReportPreferFilled(existing, normalized) : normalized
          );
        };
        aggregatePathLabReportsFromPrescriptions(prescriptions).forEach(upsertReport);
        (Array.isArray(supplementalReports) ? supplementalReports : []).forEach(upsertReport);

        const mergedReports = Array.from(byReportId.values());
        let tableRows = applyDoctorNamesToRows(
          buildOpdTableRowsFromReports(
            mergedReports,
            prescriptions,
            catalogById,
            catalogByName,
            patientNameById
          ),
          prescriptions
        );
        // Re-apply values saved in this session so staged billed/org merges cannot wipe them.
        if (recentlySavedOpdResultsRef.current.size > 0) {
          tableRows = tableRows.map((row) => {
            const saved = recentlySavedOpdResultsRef.current.get(
              Number(row.reportId ?? row.id)
            );
            if (!saved) return row;
            return {
              ...row,
              result: saved.result,
              resultStatus: saved.resultStatus || row.resultStatus || "COMPLETE",
            };
          });
        }
        setOpdTableRows(
          completedDoctorByReportId?.size
            ? mergeCompletedDoctorNamesOntoRows(tableRows, completedDoctorByReportId)
            : tableRows
        );
        opdTableRowCountRef.current = Array.isArray(tableRows) ? tableRows.length : 0;
      };

      // Await billed + full org lists together, then apply once.
      // Previously: apply([]) then apply(billed) cleared older rows before full list arrived,
      // and inFlight was cleared before background stages finished — soft refresh races left
      // only today's / billed subset until a manual refresh.
      const [billedSettled, fullSettled] = await Promise.allSettled([
        getBilledTests(),
        getOrgPathLabReportsFull(),
      ]);
      if (generation !== fetchOpdGenerationRef.current) return;

      const supplementalById = new Map();
      const upsertSupplemental = (report) => {
        const normalized = normalizePathLabReportFields(report);
        const reportId = getPathLabReportId(normalized);
        if (reportId == null) return;
        const key = Number(reportId);
        const existing = supplementalById.get(key);
        supplementalById.set(
          key,
          existing ? mergePathLabReportPreferFilled(existing, normalized) : normalized
        );
      };

      if (billedSettled.status === "fulfilled") {
        const billedList = Array.isArray(billedSettled.value) ? billedSettled.value : [];
        billedList.forEach(upsertSupplemental);
        console.log(`[OpdIpdPatients] Billed merge: ${billedList.length} path-lab reports`);
      } else {
        console.warn("[OpdIpdPatients] getBilledTests failed:", billedSettled.reason);
      }

      if (fullSettled.status === "fulfilled") {
        const fullList = Array.isArray(fullSettled.value) ? fullSettled.value : [];
        fullList.forEach(upsertSupplemental);
        console.log(`[OpdIpdPatients] Full merge: ${fullList.length} org path-lab reports`);
      } else {
        console.warn(
          "[OpdIpdPatients] getOrgPathLabReportsFull failed:",
          fullSettled.reason
        );
      }

      applyOpdData(Array.from(supplementalById.values()));
    } catch (e) {
      console.error("Error fetching OPD reports:", e);
      setOpdReports([]);
      setOpdTableRows([]);
      opdTableRowCountRef.current = 0;
    } finally {
      if (generation === fetchOpdGenerationRef.current) {
        fetchOpdInFlightRef.current = false;
        setFetching((p) => ({ ...p, opd: false }));
      }
    }
  };

  const fetchIpd = async () => {
    setFetching((p) => ({ ...p, ipd: true }));
    try {
      // Ensure organizationId is available before fetching
      const storedUser = localStorage.getItem("user");
      let organizationId = null;
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          organizationId = parsedUser?.organizationId || localStorage.getItem("organizationId");
          if (organizationId && typeof organizationId === 'string') {
            organizationId = parseInt(organizationId, 10);
          }
        } catch (e) {
          console.error("[OpdIpdPatients] Error parsing stored user:", e);
        }
      }
      
      if (!organizationId || organizationId <= 0) {
        console.warn("[OpdIpdPatients] No organizationId available, skipping IPD fetch");
        setIpdLabTests([]);
        setFetching((p) => ({ ...p, ipd: false }));
        return;
      }
      
      if (!user?.id) {
        console.warn("[OpdIpdPatients] No user ID available, skipping IPD fetch");
        setIpdLabTests([]);
        setFetching((p) => ({ ...p, ipd: false }));
        return;
      }
      
      console.log(`[OpdIpdPatients] Fetching IPD data for organizationId: ${organizationId}, userId: ${user.id}`);
      
      const data = await getAllIPDLabTests();
      console.log(`[OpdIpdPatients] Received ${Array.isArray(data) ? data.length : 0} IPD lab tests`);
      
      // Filter records to show only those assigned to the logged-in user (labId matches user.id)
      let userFilteredData = (Array.isArray(data) ? data : []).filter(row => {
        // Only show records where labId matches the logged-in user's ID
        return row.labId === user?.id;
      });
      
      // Defensive: Also filter by organizationId if backend didn't filter
      if (organizationId && userFilteredData.length > 0) {
        const orgFilteredData = userFilteredData.filter(row => {
          // If row has organizationId field, use it for filtering
          if (row.organizationId != null) {
            return Number(row.organizationId) === Number(organizationId);
          }
          // If no organizationId field, assume it's from current org (backend should filter)
          return true; // Keep it if backend didn't provide organizationId field
        });
        console.log(`[OpdIpdPatients] Filtered to ${orgFilteredData.length} IPD records for organization ${organizationId}`);
        userFilteredData = orgFilteredData;
      }
      
      console.log(`[OpdIpdPatients] Final filtered ${userFilteredData.length} IPD records for user ID: ${user?.id}`);

      // Fetch lab tests from base table (reference data for all organizations) to resolve test names
      let labTestsMap = new Map();
      try {
        const allLabTests = await getAllLabTests();
        // Create a map for quick lookup by ID
        (Array.isArray(allLabTests) ? allLabTests : []).forEach(lt => {
          if (lt.id) {
            labTestsMap.set(lt.id, lt);
          }
        });
        console.log(`[OpdIpdPatients] Loaded ${allLabTests.length} lab tests from base table for name resolution`);
      } catch (labTestError) {
        console.warn("[OpdIpdPatients] Could not load lab tests list for name resolution:", labTestError);
      }

      // Resolve test names from lab test IDs (lab tests are reference data in base table)
      const normalize = (s) => (s || '').toString().trim().toLowerCase();
      const rowsWithResolvedTestNames = userFilteredData.map(row => {
        // Resolve test name from lab test ID first, then fallback to row's test name
        let resolvedTestName = row.testName || row.test_name || row.labTestName || row.name || "—";
        
        // Try to resolve from lab test ID (testId or labTestId)
        const labTestId = row.testId || row.labTestId || row.test_id;
        if (labTestId && labTestsMap.has(labTestId)) {
          const labTest = labTestsMap.get(labTestId);
          resolvedTestName = labTest.testName || labTest.name || labTest.test_name || labTest.labTestName || resolvedTestName;
          console.log(`[OpdIpdPatients] Resolved test name from labTestId ${labTestId}: "${resolvedTestName}"`);
        } else if (resolvedTestName && resolvedTestName !== "—") {
          // Fallback: try to match by normalized name
          const normalizedRowName = normalize(resolvedTestName);
          for (const [id, labTest] of labTestsMap.entries()) {
            const ltName = normalize(labTest.testName || labTest.name || labTest.test_name || labTest.labTestName);
            if (ltName === normalizedRowName && normalizedRowName !== '') {
              resolvedTestName = labTest.testName || labTest.name || labTest.test_name || labTest.labTestName || resolvedTestName;
              console.log(`[OpdIpdPatients] Resolved test name from name match "${normalizedRowName}": "${resolvedTestName}"`);
              break;
            }
          }
        }
        
        return {
          ...row,
          resolvedTestName: resolvedTestName
        };
      });

      // Resolve doctor names using assignedBy (doctor ID)
      const itemsWithDoctorNames = await Promise.all(
        (rowsWithResolvedTestNames || []).map(async (item) => {
          const resolvedDoctorName = await getIpdDoctorName(item);
          return { ...item, resolvedDoctorName };
        })
      );

      setIpdLabTests(itemsWithDoctorNames);

      setIpdReportsByPatient(buildIpdReportsByPatientFromRows(itemsWithDoctorNames));
    } catch (e) {
      console.error("Error fetching IPD lab tests:", e);
      setIpdLabTests([]);
    } finally {
      setFetching((p) => ({ ...p, ipd: false }));
    }
  };

  // Helper: resolve doctor name for IPD item using assignedBy (doctor ID)
  const getIpdDoctorName = async (item) => {
    // First, check if assignedByName is already available and valid
    if (item?.assignedByName && item.assignedByName !== 'N/A' && item.assignedByName !== 'null' && item.assignedByName !== null && typeof item.assignedByName === 'string' && item.assignedByName.trim()) {
      return item.assignedByName.trim();
    }
    
    // Check other fallback fields
    const fallbackFields = [item?.doctorName];
    for (const field of fallbackFields) {
      if (field && field !== 'N/A' && field !== 'null' && field !== null && typeof field === 'string' && field.trim()) {
        return field.trim();
      }
    }
    
    // Get current organizationId to validate doctor belongs to this organization
    const storedUser = localStorage.getItem("user");
    const storedOrgId = localStorage.getItem("organizationId");
    let currentOrganizationId = null;
    
    if (storedOrgId) {
      try {
        currentOrganizationId = Number(storedOrgId);
        if (isNaN(currentOrganizationId) || currentOrganizationId <= 0) {
          currentOrganizationId = null;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    if (!currentOrganizationId && storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const userOrgId = user?.organizationId;
        if (userOrgId != null && userOrgId !== "" && !isNaN(userOrgId) && Number(userOrgId) > 0) {
          currentOrganizationId = Number(userOrgId);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    const doctorId = item?.assignedBy;
    if (doctorId && doctorNames[doctorId]) return doctorNames[doctorId];
    if (doctorId == null || doctorId === '' || doctorId === 'null') {
      return 'N/A';
    }
    
    try {
      const numericId = typeof doctorId === 'string' ? parseInt(doctorId, 10) : doctorId;
      if (isNaN(numericId)) return 'N/A';
      
      // Try getDoctorByUserId first (assignedBy is typically a user_id, not a doctor id)
      try {
        const doctor = await getDoctorByUserId(numericId);
        if (doctor && doctor.name) {
          // Validate organizationId if we have one - ensure doctor is from organization-specific table
          if (currentOrganizationId) {
            const doctorOrgId = doctor?.organizationId || doctor?.organization_id;
            // If doctor has no organizationId or different organizationId, reject it
            // This prevents using doctors from base table when we're in an organization context
            if (doctorOrgId == null || doctorOrgId === "" || Number(doctorOrgId) !== currentOrganizationId) {
              console.warn(`[getIpdDoctorName] Doctor found by userId but ${doctorOrgId == null ? 'has no organizationId (likely from base table)' : `belongs to organization ${doctorOrgId}, not ${currentOrganizationId}`}. Rejecting to ensure organization-specific results.`);
              // Don't use this doctor - it's from base table or different organization
              // Fall through to try getDoctorById or return N/A
            } else {
              // Doctor belongs to current organization - use it
              const name = doctor.name;
              if (name && name !== 'N/A') {
                setDoctorNames((prev) => ({ ...prev, [doctorId]: name }));
              }
              return name;
            }
          } else {
            // No organizationId to validate, use the doctor (common user scenario)
            const name = doctor.name;
            if (name && name !== 'N/A') {
              setDoctorNames((prev) => ({ ...prev, [doctorId]: name }));
            }
            return name;
          }
        }
      } catch (error) {
        // getDoctorByUserId failed (404 is expected if doctor doesn't exist with that userId)
        // Silently continue to try getDoctorById below - don't log 404 errors as they're expected
        if (error?.response?.status !== 404) {
          console.warn(`[getIpdDoctorName] getDoctorByUserId failed for ID ${doctorId}:`, error.message);
        }
      }
      
      // If getDoctorByUserId didn't return a name or failed, try getDoctorById as fallback
      // (assignedBy might be a doctorId instead of userId)
      const doctor = await getDoctorById(numericId);
      if (doctor && doctor.name) {
        // Validate organizationId if we have one - ensure doctor is from organization-specific table
        if (currentOrganizationId) {
          const doctorOrgId = doctor?.organizationId || doctor?.organization_id;
          // If doctor has no organizationId or different organizationId, reject it
          // This prevents using doctors from base table when we're in an organization context
          if (doctorOrgId == null || doctorOrgId === "" || Number(doctorOrgId) !== currentOrganizationId) {
            console.warn(`[getIpdDoctorName] Doctor found by ID but ${doctorOrgId == null ? 'has no organizationId (likely from base table)' : `belongs to organization ${doctorOrgId}, not ${currentOrganizationId}`}. Rejecting to ensure organization-specific results.`);
            // Don't use this doctor - it's from base table or different organization
            return 'N/A';
          }
        }
        const name = doctor.name;
        if (name && name !== 'N/A') {
          setDoctorNames((prev) => ({ ...prev, [doctorId]: name }));
        }
        return name;
      }
      return 'N/A';
    } catch (error) {
      // Both methods failed - this is expected if the doctor doesn't exist
      // Only log non-404 errors as 404s are expected when doctor doesn't exist
      if (error?.response?.status !== 404) {
        console.warn(`[getIpdDoctorName] Error fetching doctor with ID ${doctorId}:`, error);
      }
      return 'N/A';
    }
  };
  
  useEffect(() => {
    let active = true;
    const patientIds = (opdReports || []).map((prescription) => prescription?.patientId);
    listLabBookingsForPatients(patientIds)
      .then((bookings) => {
        if (!active) return;
        setIsTestBookedByPatient(() => createPatientBookedTestMatcher(bookings, opdReports));
      })
      .catch(() => {
        if (active) setIsTestBookedByPatient(() => () => false);
      });
    return () => {
      active = false;
    };
  }, [opdReports]);

  // Switch to OPD tab if IPD capability is not available
  useEffect(() => {
    if (!capabilitiesLoading && !hasPathIpdCapability && tab === 'ipd') {
      setTab('opd');
    }
  }, [hasPathIpdCapability, capabilitiesLoading, tab]);

    useEffect(() => {
    // Check if pathdesk has access to Path OPD capability
    if (!capabilitiesLoading && !hasPathOpdCapability) {
      // Pathdesk doesn't have access - don't fetch data
      setOpdReports([]);
      setIpdLabTests([]);
      setFetching({ opd: false, ipd: false });
      return;
    }
    
    // Wait for organizationId to be available before fetching
    // apiClient reads from localStorage, but we want to ensure it's set
    const storedUser = localStorage.getItem("user");
    let hasOrgId = false;
    let organizationId = null;
    
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        organizationId = parsedUser?.organizationId || localStorage.getItem("organizationId");
        if (organizationId && typeof organizationId === 'string') {
          organizationId = parseInt(organizationId, 10);
        }
        hasOrgId = organizationId != null && organizationId !== "" && !isNaN(organizationId) && Number(organizationId) > 0;
      } catch (e) {
        console.error("[OpdIpdPatients] Error parsing stored user in useEffect:", e);
      }
    }
    
    // Also check user object from context
    if (!hasOrgId && user?.organizationId) {
      organizationId = user.organizationId;
      hasOrgId = true;
    }
    
    console.log(`[OpdIpdPatients] useEffect - tab: ${tab}, hasOrgId: ${hasOrgId}, organizationId: ${organizationId}, userId: ${user?.id}, hasPathOpdCapability: ${hasPathOpdCapability}`);
    
    if (tab === "opd" && hasPathOpdCapability) {
      if (hasOrgId) {
        console.log(`[OpdIpdPatients] Fetching OPD data (organizationId: ${organizationId})`);
        fetchOpd();
      } else {
        console.log(`[OpdIpdPatients] Waiting for organizationId before fetching OPD data...`);
        // Wait a bit for organizationId to be set (e.g., after login)
        const timeout = setTimeout(() => {
          const recheckUser = localStorage.getItem("user");
          if (recheckUser) {
            try {
              const parsed = JSON.parse(recheckUser);
              const recheckOrgId = parsed?.organizationId || localStorage.getItem("organizationId");
              if (recheckOrgId && Number(recheckOrgId) > 0) {
                console.log(`[OpdIpdPatients] OrganizationId now available (${recheckOrgId}), fetching OPD data`);
                fetchOpd();
              } else {
                console.warn(`[OpdIpdPatients] OrganizationId still not available after timeout`);
              }
            } catch (e) {
              console.error("[OpdIpdPatients] Error parsing recheck user:", e);
            }
          }
        }, 1000); // Increased timeout to 1 second
        return () => clearTimeout(timeout);
      }
    }
    
    if (tab === "ipd" && hasPathIpdCapability) {
      if (user?.id && hasOrgId) {
        console.log(`[OpdIpdPatients] Fetching IPD data (organizationId: ${organizationId}, userId: ${user.id})`);
        fetchIpd();
      } else {
        console.log(`[OpdIpdPatients] Waiting for userId or organizationId before fetching IPD data...`);
        // Don't fetch if missing requirements
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user?.id, user?.organizationId, hasPathOpdCapability, hasPathIpdCapability, capabilitiesLoading]);

  useEffect(() => {
    const scheduleRefresh = (force = false) => {
      if (pathLabRefreshTimerRef.current) {
        clearTimeout(pathLabRefreshTimerRef.current);
      }
      pathLabRefreshTimerRef.current = setTimeout(() => {
        if (tab === "opd" && hasPathOpdCapability) {
          fetchOpd({ force, fromEvent: !force });
        } else if (tab === "ipd" && hasPathIpdCapability) {
          fetchIpd();
        }
      }, 400);
    };

    const handlePathLabRefresh = () => {
      // Soft refresh — debounce so print/save events cannot stack into a fetch loop.
      scheduleRefresh(false);
    };

    const handleVisibilityChange = () => {
      // Do not hard-refetch on every tab focus; only if data is stale.
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastOpdFetchAtRef.current < 15000) return;
      scheduleRefresh(false);
    };

    window.addEventListener(PATH_LAB_DATA_UPDATED_EVENT, handlePathLabRefresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (pathLabRefreshTimerRef.current) {
        clearTimeout(pathLabRefreshTimerRef.current);
      }
      window.removeEventListener(PATH_LAB_DATA_UPDATED_EVENT, handlePathLabRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, hasPathOpdCapability, hasPathIpdCapability]);

  const normalize = (s) => (s || '').toString().trim().toLowerCase();

  const findMatchingReportForIpd = (test) => {
    const reports = ipdReportsByPatient[test.patientId] || [];
    const targetReportId = test.reportId ?? test.report_id ?? null;
    if (targetReportId != null && targetReportId !== "") {
      const byId = reports.find(
        (r) => Number(r.reportId ?? r.id) === Number(targetReportId)
      );
      if (byId) return byId;
    }
    const name = normalize(test.resolvedTestName || test.testName);
    const targetDay = test.testDate ? dayjs(test.testDate) : (test.reportDate ? dayjs(test.reportDate) : null);
    let candidates = reports.filter((r) => normalize(r.testName) === name);
    if (targetDay) {
      candidates = candidates.filter((r) => {
        const rd = r.reportDate || r.createdAt || r.date;
        return rd ? dayjs(rd).isSame(targetDay, 'day') : true;
      });
    }
    let chosen = null;
    for (const r of candidates) {
      if (!chosen) { chosen = r; continue; }
      const rd = r.reportDate || r.createdAt || r.date;
      const cd = chosen.reportDate || chosen.createdAt || chosen.date;
      if (rd && cd && dayjs(rd).isAfter(dayjs(cd))) chosen = r;
    }
    return chosen;
  };

  // Helper function to format sub-category results for display (test-aware)
  const formatSubCategoryResults = (result, testName) => {
    const resolvedName = testName || "";
    const display = getResultDisplayRows(resolvedName, result);

    if (display.type === "table") {
      const formatted = display.rows
        .filter((row) => row.result && String(row.result).trim() !== "")
        .map((row) => `${row.name}: ${row.result}`)
        .join(", ");
      if (formatted) return formatted;
      // Do not fall back to raw foreign JSON (e.g. Typhoid IgG/IgM under Urine).
      if (hasSubCategories(resolvedName)) return "—";
    }

    if (display.simpleResult && String(display.simpleResult).trim() !== "") {
      return display.simpleResult;
    }

    if (hasSubCategories(resolvedName)) return "—";
    return result || "—";
  };

  // Helper function to extract remarks from sub-category results
  const extractRemarks = (result, testName) => {
    const display = getResultDisplayRows(testName || "", result);
    return display.remarks || "";
  };

  const deriveStatusesFromResultStatus = (resultStatus, record = null) => {
    // Try multiple possible status fields
    let statusValue = resultStatus;
    
    if (record) {
      // Check various possible status fields
      statusValue = statusValue || record.status || record.testStatus || record.paymentStatus || record.sampleStatus;
      
      // If no status field is found, try to derive from result field
      if (!statusValue && record.result !== null && record.result !== undefined && record.result !== '') {
        statusValue = 'COMPLETE'; // If result exists, assume it's complete
      }
    }
    
    const rs = String(statusValue || '').toUpperCase();
    
    let paymentStatus = 'PENDING';
    let sampleStatus = '';
    
    if (rs === 'BILLED' || rs === 'COLLECTED' || rs === 'COMPLETE' || rs === 'COMPLETED') {
      paymentStatus = 'BILLED';
    } else if (rs === 'REJECT' || rs === 'REJECTED') {
      paymentStatus = 'REJECTED';
    } else if (rs) {
      paymentStatus = rs;
    }
    
    if (rs === 'COMPLETE' || rs === 'COMPLETED') {
      sampleStatus = 'COMPLETE';
    } else if (rs === 'COLLECTED') {
      sampleStatus = 'COLLECTED';
    } else if (rs === 'PENDING' || rs === 'BILLED') {
      sampleStatus = 'PENDING';
    } else if (rs === 'REJECT' || rs === 'REJECTED') {
      sampleStatus = 'REJECTED';
    }
    
    return { paymentStatus, sampleStatus, rs };
  };

  const matchesSearchQuery = (query, values) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return values
      .filter((value) => value != null && value !== "")
      .map((value) => String(value).toLowerCase())
      .some((value) => value.includes(q));
  };

  const getLabTestSortTimestamp = (row, report = null) => {
    const candidates = [
      row?.updatedAt,
      row?.updated_at,
      report?.updatedAt,
      report?.updated_at,
      row?.reportDate,
      report?.reportDate,
      row?.testDate,
      row?.createdAt,
      report?.createdAt,
    ];

    for (const value of candidates) {
      if (!value) continue;
      const parsed = Array.isArray(value)
        ? dayjs(new Date(...value))
        : dayjs(value);
      if (parsed.isValid()) return parsed.valueOf();
    }

    return 0;
  };

  const sortLabTestsByLatest = (items, getReportForRow) =>
    [...items].sort((a, b) => {
      const timeA = getLabTestSortTimestamp(a, getReportForRow?.(a));
      const timeB = getLabTestSortTimestamp(b, getReportForRow?.(b));
      if (timeB !== timeA) return timeB - timeA;

      const reportIdA = Number(a.reportId || a.id || a.prescriptionId || 0);
      const reportIdB = Number(b.reportId || b.id || b.prescriptionId || 0);
      return reportIdB - reportIdA;
    });

  // Filter and search functions
  const dedupeOpdTableRows = (records) => {
    const byKey = new Map();
    const withoutReportId = [];

    (records || []).forEach((row) => {
      const reportId = row.reportId ?? row.id;
      if (reportId == null || reportId === "") {
        withoutReportId.push(row);
        return;
      }

      const key = `${Number(reportId)}|${getLabTestCatalogId(row) ?? normalize(row.testName)}`;
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, row);
        return;
      }

      const existingDoctor = existing.doctorName || existing.assignedByName;
      const candidateDoctor = row.doctorName || row.assignedByName;
      if (!existingDoctor && candidateDoctor) {
        byKey.set(key, row);
      }
    });

    return [...byKey.values(), ...withoutReportId];
  };

  const getFilteredOpdRecords = () => {
    const allRecords = dedupeOpdTableRows(opdTableRows || []);

    const filtered = allRecords.filter((row) => {
      const { rs } = deriveStatusesFromResultStatus(row.resultStatus, row);
      if (rs === "REJECT" || rs === "REJECTED") return false;

      // Search query filter
      if (searchQuery.trim()) {
        if (
          !matchesSearchQuery(searchQuery, [
            row.reportId || row.id || row.prescriptionId,
            row.patientId,
            row.patientName,
            row.testName,
            row.result,
            row.resultStatus,
            row.reportDate,
            row.createdAt,
            getDisplaySampleCollectedId(row, rs),
          ])
        ) {
          return false;
        }
      }

      // Patient name filter
      if (filterPatientName.trim()) {
        if (!String(row.patientName || "").toLowerCase().includes(filterPatientName.toLowerCase())) return false;
      }

      // Test name filter
      if (filterTestName.trim()) {
        if (!String(row.testName || "").toLowerCase().includes(filterTestName.toLowerCase())) return false;
      }

      // Status filter
      if (statusFilter) {
        if (rs !== statusFilter) return false;
      }

      // Date range filter
      if (fromDate || toDate) {
        const rowDate = row.createdAt || row.reportDate;
        if (!rowDate) return false;
        
        const d = new Date(rowDate);
        if (fromDate && d < new Date(fromDate)) return false;
        if (toDate) {
          const toEnd = new Date(toDate);
          toEnd.setHours(23, 59, 59, 999);
          if (d > toEnd) return false;
        }
      }

      return true;
    });

    return sortLabTestsByLatest(filtered);
  };

  const getFilteredIpdRecords = () => {
    const allRecords = (ipdLabTests || []);
    const filtered = allRecords.filter((row) => {
      const statusUpper = String(row.testStatus || "").toUpperCase();
      if (statusUpper === "REJECT" || statusUpper === "REJECTED") return false;

      // Search query filter
      if (searchQuery.trim()) {
        const report = findMatchingReportForIpd(row) || {};
        if (
          !matchesSearchQuery(searchQuery, [
            report.reportId || row.id,
            row.patientId,
            row.patientName,
            row.resolvedTestName || row.testName,
            row.testResult,
            row.testStatus,
            row.testDate || row.reportDate,
            getDisplaySampleCollectedId(row, row.testStatus),
          ])
        ) {
          return false;
        }
      }

      // Patient name filter
      if (filterPatientName.trim()) {
        if (!String(row.patientName || "").toLowerCase().includes(filterPatientName.toLowerCase())) return false;
      }

      // Test name filter
      if (filterTestName.trim()) {
        const testNameToCheck = row.resolvedTestName || row.testName || "";
        if (!String(testNameToCheck).toLowerCase().includes(filterTestName.toLowerCase())) return false;
      }

      // Status filter
      if (statusFilter) {
        if (String(row.testStatus || "") !== statusFilter) return false;
      }

      // Date range filter
      if (fromDate || toDate) {
        const rowDate = row.testDate || row.reportDate;
        if (!rowDate) return false;
        
        const d = Array.isArray(rowDate) ? new Date(...rowDate) : new Date(rowDate);
        if (fromDate && d < new Date(fromDate)) return false;
        if (toDate) {
          const toEnd = new Date(toDate);
          toEnd.setHours(23, 59, 59, 999);
          if (d > toEnd) return false;
        }
      }

      return true;
    });

    return sortLabTestsByLatest(filtered, findMatchingReportForIpd);
  };

  const getFilteredOpdPrescriptions = () => {
    const filtered = (opdReports || []).filter((prescription) => {
      const labTests = (prescription.patientLabTests || []).filter((test) => {
        const { rs } = deriveStatusesFromResultStatus(test.resultStatus, test);
        return rs !== "REJECT" && rs !== "REJECTED";
      });
      if (!labTests.length) return false;

      if (searchQuery.trim()) {
        if (
          !matchesSearchQuery(searchQuery, [
            prescription.id,
            prescription.patientId,
            prescription.patientName,
            prescription.doctorName,
            prescription.createdAt,
            ...labTests.flatMap((test) => [
              test.testName,
              test.result,
              test.resultStatus,
              test.reportId,
              getDisplaySampleCollectedId({
                ...test,
                patientId: prescription.patientId,
                prescriptionId: prescription.id,
              }, test.resultStatus),
            ]),
          ])
        ) {
          return false;
        }
      }

      if (filterPatientName.trim()) {
        if (!String(prescription.patientName || "").toLowerCase().includes(filterPatientName.toLowerCase())) {
          return false;
        }
      }

      if (filterTestName.trim()) {
        if (
          !labTests.some((test) =>
            String(test.testName || "").toLowerCase().includes(filterTestName.toLowerCase())
          )
        ) {
          return false;
        }
      }

      if (statusFilter) {
        if (
          !labTests.some((test) => {
            const { rs } = deriveStatusesFromResultStatus(test.resultStatus, test);
            return rs === statusFilter;
          })
        ) {
          return false;
        }
      }

      if (fromDate || toDate) {
        const rowDate = prescription.createdAt;
        if (!rowDate) return false;
        const d = new Date(rowDate);
        if (fromDate && d < new Date(fromDate)) return false;
        if (toDate) {
          const toEnd = new Date(toDate);
          toEnd.setHours(23, 59, 59, 999);
          if (d > toEnd) return false;
        }
      }

      return true;
    });

    return filtered.sort((a, b) => (b.id || 0) - (a.id || 0));
  };

  const getFilteredIpdGroups = () => {
    const groups = groupIpdTests(ipdLabTests || []);
    const filtered = groups.filter((group) => {
      const labTests = (group.labTests || []).filter((test) => {
        const statusUpper = String(test.testStatus || "").toUpperCase();
        return statusUpper !== "REJECT" && statusUpper !== "REJECTED";
      });
      if (!labTests.length) return false;

      if (searchQuery.trim()) {
        if (
          !matchesSearchQuery(searchQuery, [
            group.patientId,
            group.patientName,
            group.doctorName,
            group.assignedByName,
            group.testDate,
            ...labTests.flatMap((test) => {
              const report = findMatchingReportForIpd(test) || {};
              return [
                report.reportId || test.id,
                test.patientId,
                test.resolvedTestName || test.testName,
                test.testResult,
                test.testStatus,
              ];
            }),
          ])
        ) {
          return false;
        }
      }

      if (filterPatientName.trim()) {
        if (!String(group.patientName || "").toLowerCase().includes(filterPatientName.toLowerCase())) {
          return false;
        }
      }

      if (filterTestName.trim()) {
        if (
          !labTests.some((test) =>
            String(test.resolvedTestName || test.testName || "")
              .toLowerCase()
              .includes(filterTestName.toLowerCase())
          )
        ) {
          return false;
        }
      }

      if (statusFilter) {
        if (!labTests.some((test) => String(test.testStatus || "") === statusFilter)) return false;
      }

      if (fromDate || toDate) {
        const rowDate = group.testDate;
        if (!rowDate) return false;
        const d = Array.isArray(rowDate) ? new Date(...rowDate) : new Date(rowDate);
        if (fromDate && d < new Date(fromDate)) return false;
        if (toDate) {
          const toEnd = new Date(toDate);
          toEnd.setHours(23, 59, 59, 999);
          if (d > toEnd) return false;
        }
      }

      return true;
    });

    return filtered.sort((a, b) => {
      const idA = a.labTests?.[0]?.id || 0;
      const idB = b.labTests?.[0]?.id || 0;
      return idB - idA;
    });
  };
  
    const clearFilters = () => {
      setSearchQuery("");
      setFilterPatientName("");
      setFilterTestName("");
    setStatusFilter("");
      setFromDate("");
      setToDate("");
    setPageOpd(0);
    setPageIpd(0);
    };
  
    const handleExport = async () => {
    const isOpd = tab === "opd";
    const rows = isOpd ? getFilteredOpdRecords() : getFilteredIpdRecords();

    let headers;
    let tableRows;

    if (isOpd) {
      headers = ["Report Id", "Patient ID", "Test Name", "Patient Name", "Result", "Remarks", "Report Date", "Payment Status", "Sample Collection Status", "Sample ID"];
      tableRows = rows.map((r) => {
        const { paymentStatus, sampleStatus } = deriveStatusesFromResultStatus(r.resultStatus, r);
        return [
          r.reportId || r.id || r.prescriptionId,
          r.patientId ?? "",
          r.testName,
          r.patientName,
          r.result,
          extractRemarks(r.result, r.testName),
          r.reportDate || r.createdAt ? dayjs(r.reportDate || r.createdAt).format("YYYY-MM-DD") : "",
          paymentStatus,
          sampleStatus,
          getDisplaySampleCollectedId(r, sampleStatus || r.resultStatus),
        ];
      });
    } else {
      headers = ["Report Id", "Patient ID", "Test Name", "Patient Name", "Test Result", "Test Status", "Report Date", "Sample ID"];
      tableRows = rows.map((r) => {
        const report = findMatchingReportForIpd(r) || {};
        return [
          report.reportId || r.id,
          r.patientId ?? "",
          r.resolvedTestName || r.testName,
          r.patientName,
          r.testResult,
          r.testStatus,
          r.testDate || r.reportDate
            ? dayjs(
                Array.isArray(r.testDate || r.reportDate)
                  ? new Date(...(r.testDate || r.reportDate))
                  : new Date(r.testDate || r.reportDate)
              ).format("YYYY-MM-DD")
            : "",
          getDisplaySampleCollectedId(r, r.testStatus),
        ];
      });
    }

    await downloadStyledTableExcel({
      filename: `${tab}_lab_tests_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: isOpd ? "OPD Lab Tests" : "IPD Lab Tests",
      title: isOpd ? "OPD Lab Tests" : "IPD Lab Tests",
      organizationName: getOrganizationName(user),
      tableHeaders: headers,
      tableRows,
    });
    };
  
  const findOpdPrescriptionForRecord = (record) => {
    if (!record) return null;

    const recordPatientId = record.patientId ?? record.patient_id ?? null;
    const recordReportId = getPathLabReportId(record) ?? record.reportId ?? null;
    const recordPrescId = record.prescriptionId ?? record.prescription_id ?? null;

    // Prefer prescriptionId + patientId (never match prescription.id === report.id).
    if (recordPrescId != null && recordPrescId !== "") {
      const byPrescriptionId = (opdReports || []).find((prescription) => {
        const samePresc =
          String(prescription.id) === String(recordPrescId) ||
          Number(prescription.id) === Number(recordPrescId);
        if (!samePresc) return false;
        if (recordPatientId == null) return true;
        return Number(prescription.patientId) === Number(recordPatientId);
      });
      if (byPrescriptionId) return byPrescriptionId;
    }

    // Match by reportId scoped to patientId — use bound reportId only (not pathology PK).
    if (recordReportId != null && recordReportId !== "") {
      const byReportId = (opdReports || []).find((prescription) => {
        if (
          recordPatientId != null &&
          Number(prescription.patientId) !== Number(recordPatientId)
        ) {
          return false;
        }
        return (prescription.patientLabTests || []).some((test) => {
          const boundId = getBoundPathLabReportId(test);
          return boundId != null && Number(boundId) === Number(recordReportId);
        });
      });
      if (byReportId) return byReportId;
    }

    // Last resort: same patientId + test name (never name alone — duplicate names).
    if (recordPatientId == null) return null;
    return (
      (opdReports || []).find(
        (prescription) =>
          Number(prescription.patientId) === Number(recordPatientId) &&
          (prescription.patientLabTests || []).some(
            (test) => normalize(test.testName) === normalize(record.testName)
          )
      ) || null
    );
  };

  /** Lab tests for invoice print — same prescription/visit only (from table rows, not merged prescription blob). */
  const buildOpdPrintSiblingTests = (record, prescription) => {
    if (!record) return [];

    const prescId =
      record.prescriptionId ??
      record.prescription_id ??
      prescription?.id ??
      prescription?.prescriptionId ??
      null;
    const patientId = Number(
      record.patientId ?? record.patient_id ?? prescription?.patientId ?? NaN
    );

    if (prescId != null && prescId !== "" && !Number.isNaN(patientId)) {
      const scopedRows = dedupeOpdTableRows(opdTableRows || []).filter((row) => {
        if (Number(row.patientId ?? row.patient_id) !== patientId) return false;
        const rowPresc = row.prescriptionId ?? row.prescription_id ?? null;
        return rowPresc != null && String(rowPresc) === String(prescId);
      });

      if (scopedRows.length > 0) {
        return scopedRows
          .map((row) => ({
            testName:
              row.testName ||
              row.resolvedTestName ||
              row.labTestName ||
              row.test_name ||
              "",
            labTestId:
              getLabTestCatalogId(row) ??
              row.labTestId ??
              row.lab_test_id ??
              row.testId ??
              row.test_id ??
              null,
            amount:
              Number(row.amount ?? row.price ?? row.rate ?? row.testAmount ?? 0) ||
              0,
            reportId: getPathLabReportId(row) ?? row.reportId ?? row.id ?? null,
          }))
          .filter((item) => String(item.testName || "").trim());
      }
    }

    if (prescription?.patientLabTests?.length) {
      return prescription.patientLabTests.filter(Boolean);
    }

    const singleName =
      record.testName || record.resolvedTestName || record.labTestName || "";
    if (!String(singleName).trim()) return [];

    return [
      {
        testName: singleName,
        labTestId:
          getLabTestCatalogId(record) ??
          record.labTestId ??
          record.lab_test_id ??
          record.testId ??
          record.test_id ??
          null,
        amount:
          Number(record.amount ?? record.price ?? record.rate ?? record.testAmount ?? 0) ||
          0,
        reportId: getPathLabReportId(record) ?? record.reportId ?? record.id ?? null,
      },
    ];
  };

  const findIpdGroupForRecord = (record) => {
    if (!record) return null;

    const groups = groupIpdTests(ipdLabTests || []);
    const matchedGroup = groups.find((group) =>
      (group.labTests || []).some((test) => test.id === record.id)
    );
    if (matchedGroup) return matchedGroup;

    return {
      key: `ipd-${record.id}`,
      patientId: record.patientId,
      patientName: record.patientName,
      admissionId: record.admissionId,
      testDate: record.testDate,
      assignedBy: record.assignedBy,
      assignedByName: record.resolvedDoctorName || record.assignedByName || record.doctorName,
      doctorName: record.resolvedDoctorName || record.doctorName || record.assignedByName,
      labTests: [record],
    };
  };

  const prepareOpdWorkflowPrescription = (record) => {
    const prescription = findOpdPrescriptionForRecord(record);
    if (!prescription) return { prescription: null, blocked: false };
    return { prescription, blocked: false };
  };

  const handleBill = async (record) => {
    const { prescription, blocked } = prepareOpdWorkflowPrescription(record);
    if (!prescription) {
      alert("Could not find prescription for this test.");
      return;
    }
    if (blocked) {
      alert(
        "This test was not booked by the patient. Bill, collect sample, and add result are done only for the tests the patient booked."
      );
      return;
    }

    const processingKey = prescription.id || record.prescriptionId;
    setBillProcessingKey(processingKey);
    try {
      const result = await billOpdPrescription(prescription, user);
      if (!result) return;
      setInvoiceDetails(result.invoiceDetails);
      setSelectedTest(result.selectedTest);
      setOpenInvoiceModal(true);
    } catch (error) {
      console.error("Error in handleBill:", error);
      alert("Error preparing lab test invoice. Please check the console for details.");
    } finally {
      setBillProcessingKey(null);
    }
  };

  const closeResultModals = () => {
    setOpenOpdResultsModal(false);
    setOpenIpdResultsModal(false);
  };

  const augmentTestRecord = (record) => ({
    ...record,
    testName: record.resolvedTestName || record.testName || record.test_name || record.labTestName,
    assignedByName: record.resolvedDoctorName || record.assignedByName,
    doctorName: record.resolvedDoctorName || record.doctorName || record.assignedByName,
    assignedBy: record.assignedBy,
  });

  const getPrintInvoiceKey = (record, mode = tab) => {
    if (!record) return null;
    if (mode === "opd") {
      return `opd-${record.reportId || record.id || record.prescriptionId || record.patientId}`;
    }
    return `ipd-${record.id || record.reportId || record.patientId}`;
  };

  const getPrintReportKey = (record, mode = tab) => {
    if (!record) return null;
    const reportId = record.reportId || record.id || record.prescriptionId || record.patientId;
    return `${mode}-report-${reportId}`;
  };

  const handlePrintLabReport = async (record, mode = tab) => {
    if (!record || printingReportKey != null) return;

    const matchedReport = mode !== "opd" ? findMatchingReportForIpd(record) || {} : {};
    const result =
      record.result ??
      record.testResult ??
      matchedReport.result ??
      getPathLabReportResultValue(record) ??
      getPathLabReportResultValue(matchedReport);

    const reportPayload = {
      ...matchedReport,
      ...record,
      testName:
        record.testName ||
        record.resolvedTestName ||
        matchedReport.testName ||
        "",
      result,
      reportDate:
        record.reportDate ||
        record.testDate ||
        matchedReport.reportDate ||
        matchedReport.createdAt ||
        null,
      reportId:
        record.reportId ||
        matchedReport.reportId ||
        record.id ||
        matchedReport.id ||
        null,
      resultStatus:
        record.resultStatus ||
        record.testStatus ||
        matchedReport.resultStatus ||
        matchedReport.status ||
        null,
      normalRange: record.normalRange || matchedReport.normalRange || null,
      referenceValue: record.referenceValue || matchedReport.referenceValue || null,
      unit: record.unit || matchedReport.unit || null,
      subCategories: record.subCategories || matchedReport.subCategories || null,
      patientId: record.patientId || matchedReport.patientId || null,
      patientName: record.patientName || matchedReport.patientName || null,
      doctorName:
        record.doctorName ||
        record.resolvedDoctorName ||
        matchedReport.doctorName ||
        null,
    };

    if (!hasPrintableLabResult(reportPayload)) {
      toast.info("Print report is available once the lab result is entered.");
      return;
    }

    const key = getPrintReportKey(record, mode);
    setPrintingReportKey(key);
    try {
      let patient = {
        id: reportPayload.patientId,
        name: reportPayload.patientName,
        gender: record.gender || matchedReport.gender,
        age: record.age || matchedReport.age,
        dob: record.dob || matchedReport.dob,
        mobile: record.mobile || record.phone || matchedReport.mobile,
        address: record.address || matchedReport.address,
      };

      if (reportPayload.patientId) {
        try {
          const fetched = await getPatientById(reportPayload.patientId);
          if (fetched) {
            patient = { ...patient, ...fetched };
          }
        } catch (patientError) {
          console.warn(
            "[OpdIpdPatients] Could not load patient for report print:",
            patientError
          );
        }
      }

      let hospitalAddress = "";
      let hospitalPhone = "";
      const organizationId = getOrganizationId(user);
      if (organizationId) {
        try {
          const profile = await fetchOrganizationProfile(organizationId);
          hospitalAddress = profile?.address || "";
          hospitalPhone = profile?.phone || "";
        } catch (profileError) {
          console.warn(
            "[OpdIpdPatients] Could not load org profile for report print:",
            profileError
          );
        }
      }

      printPathologyLabReport({
        report: reportPayload,
        patient,
        organizationName: getOrganizationName(user),
        hospitalAddress,
        hospitalPhone,
        referringDoctorName: reportPayload.doctorName,
      });
    } catch (error) {
      console.error("[OpdIpdPatients] Failed to print pathology report:", error);
      toast.error(error?.message || "Failed to print pathology report");
    } finally {
      setPrintingReportKey(null);
    }
  };

  const handlePrintLabInvoice = async (record, mode = tab) => {
    if (!record || printingInvoiceKey != null) return;

    const key = getPrintInvoiceKey(record, mode);
    setPrintingInvoiceKey(key);
    try {
      const matchedReport = mode !== "opd" ? findMatchingReportForIpd(record) || {} : {};
      const prescription =
        mode === "opd" ? findOpdPrescriptionForRecord(record) : null;
      const ipdGroup =
        mode !== "opd" ? findIpdGroupForRecord(augmentTestRecord(record)) : null;

      // Table rows per prescription — not the full merged prescription.patientLabTests blob.
      const siblingOpdTests =
        mode === "opd" ? buildOpdPrintSiblingTests(record, prescription) : [];

      const siblingIpdTests =
        mode !== "opd" ? ipdGroup?.labTests || [record] : [];

      await printPathologyLabInvoiceFromRecord(
        {
          patientId: record.patientId || prescription?.patientId || ipdGroup?.patientId,
          patientName:
            record.patientName || prescription?.patientName || ipdGroup?.patientName,
          doctorName:
            record.doctorName ||
            record.resolvedDoctorName ||
            prescription?.doctorName ||
            matchedReport.doctorName ||
            ipdGroup?.doctorName,
          testName:
            record.testName ||
            record.resolvedTestName ||
            matchedReport.testName,
          labTestId:
            record.labTestId ||
            record.lab_test_id ||
            record.testId ||
            getLabTestCatalogId(record) ||
            matchedReport.labTestId ||
            matchedReport.testId,
          reportId:
            getPathLabReportId(record) ||
            record.reportId ||
            matchedReport.reportId ||
            record.id ||
            matchedReport.id,
          prescriptionId:
            record.prescriptionId ??
            record.prescription_id ??
            prescription?.id ??
            prescription?.prescriptionId ??
            null,
          appointmentId:
            record.appointmentId ??
            record.appointment_id ??
            prescription?.appointmentId ??
            prescription?.appointment_id ??
            null,
          amount: record.amount ?? record.price ?? record.rate ?? null,
          gender: record.gender,
          dob: record.dob,
          age: record.age,
          mobile: record.mobile || record.phone,
          address: record.address,
          patientLabTests: siblingOpdTests,
          labTests: siblingIpdTests,
        },
        { user }
      );
    } catch (error) {
      console.error("[OpdIpdPatients] Failed to print pathology invoice:", error);
      toast.error(error?.message || "Failed to print pathology invoice");
    } finally {
      setPrintingInvoiceKey(null);
    }
  };

  const handleCollectSample = (record) => {
    if (tab === "opd") {
      const { prescription, blocked } = prepareOpdWorkflowPrescription(record);
      if (!prescription) {
        alert("Could not find prescription for this test.");
        return;
      }
      if (blocked) {
        alert(
          "This test was not booked by the patient. Bill, collect sample, and add result are done only for the tests the patient booked."
        );
        return;
      }
      setSelectedTest(prescription);
      setOpenCollectSampleModal(true);
      return;
    }

    const group = findIpdGroupForRecord(augmentTestRecord(record));
    setSelectedIpdGroup(group);
    setOpenIpdCollectSampleModal(true);
  };

  const handleEditResult = (record, mode) => {
    const activeMode = mode || tab;

    closeResultModals();

    if (activeMode === "opd") {
      const { prescription, blocked } = prepareOpdWorkflowPrescription(record);
      if (!prescription) {
        alert("Could not find prescription for this test.");
        return;
      }
      if (blocked) {
        alert(
          "This test was not booked by the patient. Bill, collect sample, and add result are done only for the tests the patient booked."
        );
        return;
      }
      setSelectedTest({
        ...prescription,
        _focusTestName: record.testName,
        _focusReportId:
          getPathLabReportId(record) ?? record.reportId ?? null,
        _focusLabTestId:
          getLabTestCatalogId(record) ?? record.labTestId ?? record.testId ?? null,
      });
      setOpenOpdResultsModal(true);
      return;
    }

    const group = findIpdGroupForRecord(augmentTestRecord(record));
    setSelectedIpdGroup({ ...group, _focusTestId: record.id });
    setOpenIpdResultsModal(true);
  };

  const handleSampleCollected = async () => {
    if (tab === "opd") {
      await fetchOpd({ force: true });
      setOpenCollectSampleModal(false);
      setSelectedTest(null);
    } else {
      await fetchIpd();
      setOpenIpdCollectSampleModal(false);
      setSelectedIpdGroup(null);
    }
  };

  const refetchCurrentTab = async () => {
    invalidateOrgPathLabReportsLiteCache();
    if (tab === "opd") await fetchOpd({ force: true });
    else await fetchIpd();
  };

  const handleOpdResultSubmitted = async (saved) => {
    // Drop stale org caches before queue refresh so table shows saved results.
    invalidateOrgPathLabReportsLiteCache();

    // Remember saved payloads so staged fetchOpd merges cannot overwrite with stale org data.
    const updates = Array.isArray(saved?.updates) ? saved.updates : [];
    updates.forEach((update) => {
      if (update?.reportId == null) return;
      recentlySavedOpdResultsRef.current.set(Number(update.reportId), {
        result: update.result,
        resultStatus: update.resultStatus || "COMPLETE",
      });
    });

    if (updates.length > 0) {
      setOpdTableRows((prev) =>
        (Array.isArray(prev) ? prev : []).map((row) => {
          const match = updates.find(
            (update) => Number(update.reportId) === Number(row.reportId ?? row.id)
          );
          if (!match) return row;
          return {
            ...row,
            result: match.result,
            resultStatus: match.resultStatus || "COMPLETE",
          };
        })
      );
    }

    // Authoritative patient API (same source as edit prefill), then full refresh.
    const patientId = saved?.patientId;
    if (patientId != null && patientId !== "") {
      try {
        const freshReports = await getPathPabReportByPatient(patientId);
        if (Array.isArray(freshReports) && freshReports.length > 0) {
          setOpdTableRows((prev) => {
            const rows = Array.isArray(prev) ? [...prev] : [];
            const indexById = new Map(
              rows.map((row, index) => [Number(row.reportId ?? row.id), index])
            );
            freshReports.forEach((report) => {
              const normalized = normalizePathLabReportFields({
                ...report,
                patientId: report?.patientId ?? report?.patient_id ?? patientId,
              });
              const reportId = getPathLabReportId(normalized);
              if (reportId == null) return;
              const idx = indexById.get(Number(reportId));
              if (idx == null) return;
              const existing = rows[idx];
              const merged = mergePathLabReportPreferFilled(existing, normalized);
              const nextResult = getPathLabReportResultValue(merged);
              rows[idx] = {
                ...existing,
                ...merged,
                result: nextResult,
                resultStatus:
                  merged.resultStatus ?? merged.result_status ?? existing.resultStatus,
              };
              // Patient API confirmed — keep overlay until next navigation; refresh map entry.
              if (nextResult != null && String(nextResult).trim() !== "") {
                recentlySavedOpdResultsRef.current.set(Number(reportId), {
                  result: nextResult,
                  resultStatus:
                    merged.resultStatus ?? merged.result_status ?? "COMPLETE",
                });
              }
            });
            return rows;
          });
        }
      } catch (error) {
        console.warn(
          `[OpdIpdPatients] Post-save patient report refresh failed for ${patientId}:`,
          error
        );
      }
    }

    await refetchCurrentTab();
    notifyPathLabDataUpdated();
    setOpenOpdResultsModal(false);
    setSelectedTest(null);
  };

  const handleIpdResultSubmitted = async () => {
    invalidateOrgPathLabReportsLiteCache();
    await refetchCurrentTab();
    notifyPathLabDataUpdated();
    setOpenIpdResultsModal(false);
    setSelectedIpdGroup(null);
  };

  const handleRejectTest = async (record) => {
    if (tab === "ipd") {
      try {
        await updateRejectStatus(record.id);
        // Refresh the data after successful update
        await fetchIpd();
      } catch (error) {
        console.error("Error rejecting test:", error);
        // You can add a toast notification here if needed
      }
    }
  };

    return (
    <Box sx={{ width: "100%" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box sx={PAGE_TABS_BAR_SX}>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            {...PAGE_TAB_LIST_PROPS}
          >
            {hasPathOpdCapability && (
              <Tab value="opd" label="OPD" />
            )}
            {hasPathIpdCapability && (
              <Tab value="ipd" label="IPD" />
            )}
          </Tabs>
        </Box>
      </Box>

      {!capabilitiesLoading && !hasPathOpdCapability && (
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">Pathology Admin access is not available for your subscription package.</Alert>
        </Box>
      )}
      {hasPathOpdCapability && tab === "opd" && (
        <Box>
          {fetching.opd ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress size={80} />
            </Box>
          ) : (opdReports || []).length > 0 ? (
            <Paper>
          <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
            <Box sx={TABLE_SEARCH_ROW_SX}>
              <TextField
                placeholder="Search by patient ID, name, report ID, test..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                      startAdornment: (<InputAdornment position="start"><SearchIcon sx={TABLE_ICON_SX} fontSize="small"/></InputAdornment>) 
                    }} 
                  />
                  <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
                    <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={() => setShowFilters(v => !v)}>
                      <FilterAltIcon fontSize="small"/>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export to Excel">
                    <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                      <DownloadIcon fontSize="small"/>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Refresh">
                    <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={async () => await fetchOpd({ force: true })}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {showFilters && (
                  <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1 }}>
                    <TextField 
                      label="Patient name" 
                      value={filterPatientName} 
                      onChange={(e) => setFilterPatientName(e.target.value)} 
                    />
                    <TextField 
                      label="Test name" 
                      value={filterTestName} 
                      onChange={(e) => setFilterTestName(e.target.value)} 
                    />
                    <TextField 
                      label="Status" 
                      value={statusFilter} 
                      onChange={(e) => setStatusFilter(e.target.value)} 
                      select 
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="PENDING">PENDING</MenuItem>
                      <MenuItem value="BILLED">BILLED</MenuItem>
                      <MenuItem value="COLLECTED">COLLECTED</MenuItem>
                      <MenuItem value="COMPLETE">COMPLETE</MenuItem>
                    </TextField>
                    <TextField 
                      label="From date" 
                      type="date" 
                      value={fromDate} 
                      onChange={(e) => setFromDate(e.target.value)} 
                      InputLabelProps={{ shrink: true }} 
                    />
                    <TextField 
                      label="To date" 
                      type="date" 
                      value={toDate} 
                      onChange={(e) => setToDate(e.target.value)} 
                      InputLabelProps={{ shrink: true }} 
                    />
                    <Tooltip title="Clear filters">
                      <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                        <ClearAllIcon fontSize="small"/>
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </Box>
              <TableContainer sx={{ maxHeight: 600, overflowX: "auto" }}>
                <Table stickyHeader size="small" sx={pathTableSx}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell sx={colW.report}>Report Id</StyledTableCell>
                      <StyledTableCell sx={colW.patientId}>Patient ID</StyledTableCell>
                      <StyledTableCell sx={colW.patientName}>Patient Name</StyledTableCell>
                      <StyledTableCell sx={colW.doctor}>Doctor</StyledTableCell>
                      <StyledTableCell sx={colW.testName}>Test Name</StyledTableCell>
                      <StyledTableCell sx={colW.result}>Result</StyledTableCell>
                      <StyledTableCell sx={colW.remarks}>Remarks</StyledTableCell>
                      <StyledTableCell sx={colW.date}>Report Date</StyledTableCell>
                      <StyledTableCell sx={colW.payment}>Payment</StyledTableCell>
                      <StyledTableCell sx={colW.sampleStatus}>Sample Status</StyledTableCell>
                      <StyledTableCell sx={colW.sampleId}>Sample ID</StyledTableCell>
                      <StyledTableCell sx={colW.action}>Action</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredOpdRecords()
                      .slice(pageOpd * rowsPerPageOpd, pageOpd * rowsPerPageOpd + rowsPerPageOpd)
                      .map((r) => {
                        const { paymentStatus, sampleStatus, rs } = deriveStatusesFromResultStatus(r.resultStatus, r);
                        const canAddResult =
                          (paymentStatus === "BILLED" && sampleStatus === "COLLECTED" && rs === "COLLECTED") ||
                          rs === "COMPLETE";
                        const canPrintInvoice = paymentStatus === "BILLED";
                        const canPrintReport = hasPrintableLabResult(r);
                        const invoicePrintKey = getPrintInvoiceKey(r, "opd");
                        const reportPrintKey = getPrintReportKey(r, "opd");
                        const isPrintingInvoice =
                          printingInvoiceKey != null &&
                          String(printingInvoiceKey) === String(invoicePrintKey);
                        const isPrintingReport =
                          printingReportKey != null &&
                          String(printingReportKey) === String(reportPrintKey);
                        return (
                          <TableRow hover key={`${r.prescriptionId}-${r.testName}-${r.reportId || r.id}`}>
                            <TableCell sx={{ ...colW.report, ...ellipsisCellSx }}>{r.reportId || r.id || r.prescriptionId}</TableCell>
                            <TableCell sx={{ ...colW.patientId, ...ellipsisCellSx }}>{r.patientId ?? "—"}</TableCell>
                            <TableCell sx={{ ...colW.patientName, ...ellipsisCellSx }}>
                              <Tooltip title="Open patient timeline">
                                <span
                                  style={{ cursor: "pointer", color: "#1976d2" }}
                                  onClick={() => {
                                    if (r.patientId) {
                                      setTimelinePatientId(r.patientId);
                                      setOpenTimeline(true);
                                    }
                                  }}
                                >
                                  {r.patientName}
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ ...colW.doctor, ...ellipsisCellSx }}>{r.doctorName || "—"}</TableCell>
                            <TableCell sx={{ ...colW.testName, ...ellipsisCellSx }} title={r.testName}>
                              <Box>
                                {r.testName}
                                {(() => {
                                  const booked =
                                    isTestBookedByPatient(r) ||
                                    (r.bookedByPatient
                                      ? { whenLabel: r.whenLabel || r.preferredTime || "" }
                                      : null);
                                  return booked ? (
                                    <BookedByPatientMark detail={booked.whenLabel || r.whenLabel || ""} />
                                  ) : null;
                                })()}
                              </Box>
                            </TableCell>
                            <TableCell sx={colW.result}>
                              <Box sx={{ maxWidth: 240, wordBreak: "break-word", maxHeight: 48, overflow: "hidden" }}>
                                {formatSubCategoryResults(r.result, r.testName)}
                              </Box>
                            </TableCell>
                            <TableCell sx={colW.remarks}>
                              <Box sx={{ wordBreak: "break-word" }}>
                                {extractRemarks(r.result, r.testName)}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ ...colW.date, ...ellipsisCellSx }}>{r.reportDate ? dayjs(r.reportDate).format("D MMM YYYY") : ""}</TableCell>
                            <TableCell sx={colW.payment}>
                              {paymentStatus === "PENDING" ? (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  onClick={() => handleBill(r)}
                                  disabled={billProcessingKey === (r.prescriptionId || r.id)}
                                  sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: "0.7rem" }}
                                >
                                  {billProcessingKey === (r.prescriptionId || r.id) ? "Processing..." : "Bill"}
                                </Button>
                              ) : (
                                paymentStatus
                              )}
                            </TableCell>
                            <TableCell sx={colW.sampleStatus}>
                              {paymentStatus === "BILLED" && sampleStatus === "PENDING" ? (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="secondary"
                                  onClick={() => handleCollectSample(r)}
                                  sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: "0.7rem" }}
                                >
                                  Collect
                                </Button>
                              ) : (
                                sampleStatus
                              )}
                            </TableCell>
                            <TableCell sx={{ ...colW.sampleId, ...ellipsisCellSx }} title={getDisplaySampleCollectedId(r, sampleStatus || rs) || ""}>
                              {getDisplaySampleCollectedId(r, sampleStatus || rs) || "—"}
                            </TableCell>
                            <TableCell sx={colW.action}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                                <Tooltip
                                  title={
                                    canPrintInvoice
                                      ? "Print invoice"
                                      : "Print invoice available after billing"
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      sx={actionIconBtnSx}
                                      disabled={!canPrintInvoice || printingInvoiceKey != null}
                                      onClick={() => handlePrintLabInvoice(r, "opd")}
                                    >
                                      {isPrintingInvoice ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <PrintIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    canPrintReport
                                      ? "Print report"
                                      : "Print report available after result is entered"
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      sx={actionIconBtnSx}
                                      disabled={!canPrintReport || printingReportKey != null}
                                      onClick={() => handlePrintLabReport(r, "opd")}
                                    >
                                      {isPrintingReport ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <DescriptionOutlinedIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={canAddResult ? "Add Result" : "Enabled after collection"}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      sx={actionIconBtnSx}
                                      disabled={!canAddResult}
                                      onClick={() => handleEditResult(r, "opd")}
                                    >
                                      <ModeEditOutlineTwoToneIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={getFilteredOpdRecords().length}
                rowsPerPage={rowsPerPageOpd}
                page={pageOpd}
                onPageChange={(e, p) => setPageOpd(p)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPageOpd(+e.target.value);
                  setPageOpd(0);
                }}
              />
            </Paper>
          ) : (
            <Alert severity="info">No OPD tests found</Alert>
            )}
          </Box>
      )}

      {hasPathIpdCapability && tab === "ipd" && (
        <Box>
          {!user?.id ? (
            <Alert severity="warning">Please log in to view IPD Lab Tests</Alert>
          ) : fetching.ipd ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress size={80} />
            </Box>
          ) : (ipdLabTests || []).length > 0 ? (
            <Paper>
              <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
                <Box sx={TABLE_SEARCH_ROW_SX}>
                  <TextField 
                    placeholder="Search by patient ID, name, report ID, test..." 
                    value={searchQuery} 
                    onChange={(e) => setSearchQuery(e.target.value)} 
                    fullWidth
                    size="small"
                    InputProps={{ 
                      startAdornment: (<InputAdornment position="start"><SearchIcon sx={TABLE_ICON_SX} fontSize="small"/></InputAdornment>) 
                }}
              />
              <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
                    <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={() => setShowFilters(v => !v)}>
                      <FilterAltIcon fontSize="small"/>
                </IconButton>
              </Tooltip>
              <Tooltip title="Export to Excel">
                <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                      <DownloadIcon fontSize="small"/>
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={async () => await fetchIpd()}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {showFilters && (
            <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1 }}>
                    <TextField 
                      label="Patient name" 
                      value={filterPatientName} 
                      onChange={(e) => setFilterPatientName(e.target.value)} 
                    />
                    <TextField 
                      label="Test name" 
                      value={filterTestName} 
                      onChange={(e) => setFilterTestName(e.target.value)} 
                    />
                    <TextField 
                      label="Status" 
                      value={statusFilter} 
                      onChange={(e) => setStatusFilter(e.target.value)} 
                      select 
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {[...new Set((ipdLabTests || []).map(r => r.testStatus).filter(Boolean))].map(s => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </TextField>
                    <TextField 
                      label="From date" 
                      type="date" 
                      value={fromDate} 
                      onChange={(e) => setFromDate(e.target.value)} 
                      InputLabelProps={{ shrink: true }} 
                    />
                    <TextField 
                      label="To date" 
                      type="date" 
                      value={toDate} 
                      onChange={(e) => setToDate(e.target.value)} 
                      InputLabelProps={{ shrink: true }} 
                    />
              <Tooltip title="Clear filters">
                <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                        <ClearAllIcon fontSize="small"/>
                </IconButton>
              </Tooltip>
            </Stack>
            )}
          </Box>
              <TableContainer sx={{ maxHeight: 600, overflowX: "auto" }}>
                <Table stickyHeader size="small" sx={pathTableSx}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell sx={colW.report}>Report Id</StyledTableCell>
                      <StyledTableCell sx={colW.patientId}>Patient ID</StyledTableCell>
                      <StyledTableCell sx={colW.patientName}>Patient Name</StyledTableCell>
                      <StyledTableCell sx={colW.doctor}>Doctor</StyledTableCell>
                      <StyledTableCell sx={colW.testName}>Test Name</StyledTableCell>
                      <StyledTableCell sx={colW.result}>Result</StyledTableCell>
                      <StyledTableCell sx={colW.remarks}>Remarks</StyledTableCell>
                      <StyledTableCell sx={colW.date}>Report Date</StyledTableCell>
                      <StyledTableCell sx={colW.sampleStatus}>Sample Status</StyledTableCell>
                      <StyledTableCell sx={colW.sampleId}>Sample ID</StyledTableCell>
                      <StyledTableCell sx={colW.action}>Action</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredIpdRecords()
                      .slice(pageIpd * rowsPerPageIpd, pageIpd * rowsPerPageIpd + rowsPerPageIpd)
                      .map((test) => {
                        const report = findMatchingReportForIpd(test) || {};
                        const canAddResult = ["COLLECTED", "COMPLETED", "COMPLETE"].includes(
                          String(test.testStatus || "").toUpperCase()
                        );
                        const resultValue = test.testResult || report.result || "";
                        const statusUpper = String(test.testStatus || report.resultStatus || "").toUpperCase();
                        const canPrintInvoice = ["BILLED", "COLLECTED", "COMPLETED", "COMPLETE"].includes(
                          statusUpper
                        );
                        const canPrintReport = hasPrintableLabResult({
                          ...test,
                          ...report,
                          result: resultValue,
                          testResult: resultValue,
                        });
                        const invoicePrintKey = getPrintInvoiceKey(test, "ipd");
                        const reportPrintKey = getPrintReportKey(test, "ipd");
                        const isPrintingInvoice =
                          printingInvoiceKey != null &&
                          String(printingInvoiceKey) === String(invoicePrintKey);
                        const isPrintingReport =
                          printingReportKey != null &&
                          String(printingReportKey) === String(reportPrintKey);
                        return (
                          <TableRow
                            hover
                            key={test.id || `${test.patientId}-${test.testName}-${test.testDate || test.reportDate || ""}`}
                          >
                            <TableCell sx={{ ...colW.report, ...ellipsisCellSx }}>{report.reportId || test.id || ""}</TableCell>
                            <TableCell sx={{ ...colW.patientId, ...ellipsisCellSx }}>{test.patientId ?? "—"}</TableCell>
                            <TableCell sx={{ ...colW.patientName, ...ellipsisCellSx }}>
                              <Tooltip title="Open patient timeline">
                                <span
                                  style={{ cursor: "pointer", color: "#1976d2" }}
                                  onClick={() => {
                                    if (test.patientId) {
                                      setTimelinePatientId(test.patientId);
                                      setOpenTimeline(true);
                                    }
                                  }}
                                >
                                  {test.patientName}
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ ...colW.doctor, ...ellipsisCellSx }}>{test.resolvedDoctorName || test.assignedByName || test.doctorName || "—"}</TableCell>
                            <TableCell sx={{ ...colW.testName, ...ellipsisCellSx }} title={test.resolvedTestName || test.testName}>
                              <Box>
                                {test.resolvedTestName || test.testName || "N/A"}
                                {(() => {
                                  const booked = isTestBookedByPatient(test);
                                  return booked ? (
                                    <BookedByPatientMark detail={booked.whenLabel || ""} />
                                  ) : null;
                                })()}
                              </Box>
                            </TableCell>
                            <TableCell sx={colW.result}>
                              <Box sx={{ maxWidth: 240, wordBreak: "break-word", maxHeight: 48, overflow: "hidden" }}>
                                {formatSubCategoryResults(resultValue, test.resolvedTestName || test.testName)}
                              </Box>
                            </TableCell>
                            <TableCell sx={colW.remarks}>
                              <Box sx={{ wordBreak: "break-word" }}>
                                {extractRemarks(resultValue, test.resolvedTestName || test.testName)}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ ...colW.date, ...ellipsisCellSx }}>
                              {test.testDate
                                ? Array.isArray(test.testDate)
                                  ? dayjs(
                                      new Date(
                                        test.testDate[0],
                                        test.testDate[1] - 1,
                                        test.testDate[2],
                                        test.testDate[3] || 0,
                                        test.testDate[4] || 0
                                      )
                                    ).format("D MMM YYYY")
                                  : dayjs(test.testDate).format("D MMM YYYY")
                                : report.reportDate
                                  ? dayjs(report.reportDate).format("D MMM YYYY")
                                  : ""}
                            </TableCell>
                            <TableCell sx={colW.sampleStatus}>
                              {test.testStatus === "PENDING" || test.testStatus === null ? (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="secondary"
                                  onClick={() => handleCollectSample(test)}
                                  sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: "0.7rem" }}
                                >
                                  Collect
                                </Button>
                              ) : test.testStatus === "COMPLETED" ? (
                                "COMPLETE"
                              ) : (
                                test.testStatus
                              )}
                            </TableCell>
                            <TableCell sx={{ ...colW.sampleId, ...ellipsisCellSx }} title={getDisplaySampleCollectedId(test, test.testStatus) || ""}>
                              {getDisplaySampleCollectedId(test, test.testStatus) || "—"}
                            </TableCell>
                            <TableCell sx={colW.action}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                                <Tooltip
                                  title={
                                    canPrintInvoice
                                      ? "Print invoice"
                                      : "Print invoice available after billing/collection"
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      sx={actionIconBtnSx}
                                      disabled={!canPrintInvoice || printingInvoiceKey != null}
                                      onClick={() => handlePrintLabInvoice(test, "ipd")}
                                    >
                                      {isPrintingInvoice ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <PrintIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    canPrintReport
                                      ? "Print report"
                                      : "Print report available after result is entered"
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      sx={actionIconBtnSx}
                                      disabled={!canPrintReport || printingReportKey != null}
                                      onClick={() => handlePrintLabReport(test, "ipd")}
                                    >
                                      {isPrintingReport ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <DescriptionOutlinedIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={canAddResult ? "Add Result" : "Enabled after collection"}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      sx={actionIconBtnSx}
                                      disabled={!canAddResult}
                                      onClick={() => handleEditResult(test, "ipd")}
                                    >
                                      <ModeEditOutlineTwoToneIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
          <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
            component="div"
                count={getFilteredIpdRecords().length}
                rowsPerPage={rowsPerPageIpd}
                page={pageIpd}
                onPageChange={(e, p) => setPageIpd(p)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPageIpd(+e.target.value);
                  setPageIpd(0);
                }}
          />
        </Paper>
          ) : (
            <Alert severity="info">No IPD Lab Tests assigned to you</Alert>
          )}
        </Box>
      )}

      <Modal open={openTimeline} onClose={() => setOpenTimeline(false)}>
        <Box sx={{ ...STANDARD_MODAL_SURFACE_SX, p: 2, width: '90%' }}>
          {timelinePatientId && (
            tab === "opd" ? (
              <OpdReports 
                patientId={timelinePatientId} 
                onBack={() => setOpenTimeline(false)}
              />
            ) : (
              <IpdReports 
                patientId={timelinePatientId} 
                onBack={() => setOpenTimeline(false)}
              />
            )
          )}
        </Box>
      </Modal>

      <InvoiceModal
        open={openInvoiceModal}
        onClose={() => {
          setOpenInvoiceModal(false);
          setInvoiceDetails(null);
          setSelectedTest(null);
        }}
        invoiceDetails={invoiceDetails}
        reportId={selectedTest?.reportId}
        test={selectedTest}
        refetchPathLabTestList={refetchCurrentTab}
      />

      <CollectSampleModal
        open={openCollectSampleModal}
        onClose={() => {
          setOpenCollectSampleModal(false);
          setSelectedTest(null);
        }}
        test={selectedTest}
        onSampleCollected={handleSampleCollected}
      />

      <OpdTestResultsModal
        open={openOpdResultsModal}
        onClose={() => {
          setOpenOpdResultsModal(false);
          setSelectedTest(null);
        }}
        prescription={selectedTest}
        onSubmitted={handleOpdResultSubmitted}
      />

      <IpdWorkflowCollectModal
        open={openIpdCollectSampleModal}
        onClose={() => {
          setOpenIpdCollectSampleModal(false);
          setSelectedIpdGroup(null);
        }}
        group={selectedIpdGroup}
        onSampleCollected={handleSampleCollected}
      />

      <IpdWorkflowResultsModal
        open={openIpdResultsModal}
        onClose={() => {
          setOpenIpdResultsModal(false);
          setSelectedIpdGroup(null);
        }}
        group={selectedIpdGroup}
        onSubmitted={handleIpdResultSubmitted}
      />
    </Box>
    );
  };
  
export default OpdIpdPatients;